import _, { cloneDeep } from 'lodash'
import { APIServer, type DefectRejectedItem, type DefectSyncRejectionLog } from '@/app/data/api'
import { ApiError } from '@/app/auth'
import type { ChangedItem, QuickInputOtherItem, ShapeTemplateType } from '@/app/data/type'
import { SHAPES, type CanvasEngineExportEntityType } from '@/lib/CanvasEngine'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
// GCS_ROOT_PATH와 isApp은 SSoT 패턴 적용 후 더 이상 사용되지 않음
import { SQL } from '@/lib/Sqlite'
import { prepareSQLiteValue } from '@/lib/Sqlite/schema'
import {
  parseRow,
  isPreviousShape,
  sortByKeys,
  getLastSortKey,
  pickShapeScaleFields,
} from '@/app/util'
import { getUnixtime, getRandomString } from '@/lib/Functions'
import { Network } from '@capacitor/network'
import { useDefectPhotoStore } from './defectPhoto'
import {
  getCurrentColor,
  getCurrentColorName,
  LENGTH_VALUES,
  WIDTH_VALUES_RANGE,
  PREV_DEFECT_STYLE,
} from '@/app/constant'
import { findChangedItems, logChanges, useDeviceEventLogStore } from '@/app/store/deviceEventLog'
import { useAllTaskStore } from '@/app/store/task'
import { useCanvasStore } from '@/app/store/canvas'
import { getRelatedShapes } from '@/app/function'
import { calcBecomeCurrentColors, calcBecomePreviousColors } from '@/app/function/shapeColorToggle'
import { syncQueue, JobPriority } from '@/app/sync'
import { filterStaleMergedItems } from '@/app/sync/mergeWriteGuard'
import { hasInFlightJob, normalizeFieldUpdates } from '@/app/sync/jobGuards'
import { DependencyNotReadyError, DependencyRejectedError } from '@/app/sync/syncError'
import {
  SYNC_REJECTED_KEY,
  SYNC_REPAIRED_KEY,
  clearSyncRejection,
  isSyncRejectedActive,
  isValidationRejectStatus,
  sanitizeDefectForServer,
} from '@/app/sync/syncRejection'
import { useUndoRedo } from '../../undoRedo'
import { useAppEditorFacilityStore } from '../../store/editor'
import { SyncManager } from '@/app/sync/syncManager'
import { Defect as DefectType, DefectPhoto, CanvasNotFoundHandler } from '../types'
import SQLite from '../../../lib/Task/sqlite'
import Merge from '../../../lib/Task/merge'
import { SQLiteEventBus, SQLiteUpdateEvent } from '@/lib/sqliteEventBus'
import { isUnplacedPrevDefect } from '@/app/function/defectMode'
import { useFloorplanStore } from './floorplan'
import UTIL from '../../../lib/CanvasEngine/helper/canvasUtil'
import { App } from '../..'

export interface Defect extends DefectType {
  localStringId: string
  modifiedFromLocal: boolean
}

const storeKey = 'defects'

/** getUnplacedPrevCandidatesFromDB 의 행 (SQLite 경량 조회, 전체 Defect 아님) */
export interface UnplacedPrevCandidateRow {
  id: string
  localStringId: string
  floorplan_id: number
  previous_balloon_number: string | null
  is_current: number | null
  structural_component: string | null
  classification: string | null
  width: string | null
  length: string | null
  area: string | null
  count: string | null
  memo: string | null
  sort_key: string | null
}
const ADD_SHAPE_KEYS = [
  'shape_type',
  'shape_data',
  'memo',
  'color',
  'structural_component',
  'classification',
  'progressing',
  'width',
  'length',
  'area',
  'count',
  'is_structural',
  'repair_plan',
  'cause_estimate',
  'template',
  'previous_balloon_number',
  'sort_key',
  'admin_index_modified',
  'parent_defect_id',
  'layer_id',
  'floorplan_id',
  'test_measure_data',
  'inspection_type',
  'is_current',
  'created_at',
  // 오프라인 생성 후 동기화 시 로컬 기기 시각 보존
  'updated_at',
  'fields_updated_at',
]
const UPDATE_SHAPE_KEYS = [
  'shape_type',
  'shape_data',
  'memo',
  'memo_modified',
  'color',
  'color_modified',
  'selected_tags',
  'structural_component',
  'classification',
  'progressing',
  'width',
  'length',
  'area',
  'count',
  'layer_id',
  'is_structural',
  'repair_plan',
  'cause_estimate',
  'template',
  'previous_defect_id',
  'test_measure_data',
  'inspection_type',
  'is_current',
  'sort_key',
]

// fields_updated_at 원본(문자열/객체/빈값)을 객체로 정규화
function normalizeFuaObject(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  return raw as Record<string, unknown>
}

/**
 * 업로드 payload에 동봉할 필드별 스탬프를 만든다.
 * 로컬 스탬프가 없는 필드는 마지막 동기화 시점(updated_at)을 스탬프로 보내, 그 이후 다른 기기가
 * 바꾼 값을 이 기기의 낡은 full-row가 덮지 못하도록 서버(buildUpdateQuery 가드)가 판정할 수 있게 한다.
 * @param row 전송 직전 SQLite에서 재조회한 row
 * @returns UPDATE_SHAPE_KEYS 각 필드의 스탬프
 */
function buildUploadFieldsUpdatedAt(row: Defect): Record<string, string> {
  const local = normalizeFuaObject(row.fields_updated_at)
  const fallback = row.updated_at || row.created_at
  const result: Record<string, string> = {}
  for (const key of UPDATE_SHAPE_KEYS) {
    const stamp = local[key] ?? fallback
    if (typeof stamp === 'string' && stamp) result[key] = stamp
  }
  return result
}

/**
 * 업로드 성공 응답을 SQLite 갱신 목록으로 변환한다.
 * 서버가 필드별 스탬프 가드로 어떤 필드든 거부할 수 있으므로 응답 값 전체를 채택해야
 * 로컬(스탬프는 서버 것, 값은 낡은 것)이 서버와 영구히 어긋나지 않는다.
 * 로컬 값이 비어 있어 전송에서 제외된 필드(returnHasValue)는 기존대로 채택하지 않는다.
 * @param response 서버 응답 row
 * @param localRow 응답 시점 SQLite row (parseRow 결과, 없으면 전 필드 채택)
 * @returns individualSetLocalModified용 key/value 목록 (modifiedFromLocal=false 포함)
 */
function buildResponseAdoptUpdates(
  response: Partial<Defect> | undefined,
  localRow: Record<string, unknown> | undefined,
): { key: string; value: unknown }[] {
  const updates: { key: string; value: unknown }[] = [{ key: 'modifiedFromLocal', value: false }]
  if (!response || typeof response !== 'object') return updates
  const r = response as Record<string, unknown>
  const toSQLite = (value: unknown): unknown => {
    if (typeof value === 'boolean') return value ? 1 : 0
    if (value !== null && typeof value === 'object') return JSON.stringify(value)
    return value
  }
  // 서버 생성 메타 필드는 값이 있으면 항상 채택 (신규 결함은 로컬 fields_updated_at이 비어 있음)
  for (const key of ['fields_updated_at', 'updated_at']) {
    if (r[key] !== null && r[key] !== undefined) updates.push({ key, value: toSQLite(r[key]) })
  }
  for (const key of UPDATE_SHAPE_KEYS) {
    if (!(key in r)) continue
    const local = localRow?.[key]
    if (localRow && (local === null || local === undefined || local === '')) continue
    updates.push({ key, value: r[key] === undefined ? null : toSQLite(r[key]) })
  }
  return updates
}

export const useShapeStore = defineStore(storeKey, () => {
  const shapes = ref<Defect[]>([])
  // 현재 활성 floorplan ID (이 floorplan의 shape만 shapes.value에 보유)
  const activeFloorplanId = ref<string>('')

  // SQLite 이벤트 리스너 참조 (중복 등록 방지 및 cleanup용)
  let currentListener: ((event: SQLiteUpdateEvent) => void) | null = null

  // shape 배열을 부모자식관계로 정렬하여 새로운 배열 반환
  function sortShapesWithChildren<T extends Defect>(shapesToSort: T[]): T[] {
    // 부모 id → 자식 목록을 한 번에 묶는다 (부모마다 전체를 다시 filter하면 O(n²) — 저장마다 재계산되는 경로)
    // 키는 문자열로 맞춰 기존 `parent_defect_id == parentId` 비교와 같은 짝을 만든다. 묶음 안 순서는 입력 순서
    const parents: T[] = []
    const childrenByParentId = new Map<string, T[]>()
    for (const shape of shapesToSort) {
      if (!shape.parent_defect_id) {
        parents.push(shape)
        continue
      }
      const parentKey = String(shape.parent_defect_id)
      const siblings = childrenByParentId.get(parentKey)
      if (siblings) siblings.push(shape)
      else childrenByParentId.set(parentKey, [shape])
    }
    // 정렬은 묶음마다 한 번 (같은 id의 부모가 둘이면 같은 자식 묶음을 공유 — 안정 정렬이라 재정렬해도 순서 동일)
    for (const children of childrenByParentId.values()) sortByKeys(children)

    const result: T[] = []
    // 경로 위 id 집합: 자기 자신·순환 참조 데이터에서 무한 재귀 방지
    const path = new Set<string>()
    function appendWithChildren(shape: T): void {
      result.push(shape)
      const key = String(shape.id)
      const children = childrenByParentId.get(key)
      if (!children || path.has(key)) return
      path.add(key)
      for (const child of children) appendWithChildren(child)
      path.delete(key)
    }
    // 각 부모에 대해 자식들을 재귀적으로 정렬된 순서로 추가
    for (const parent of sortByKeys(parents)) appendWithChildren(parent)

    return result
  }

  const getCurrentRelatedShapes = (shapeId: string): Defect[] => {
    const findShape = shapes.value.find(shape => shape.localStringId!.toString() == shapeId)
    if (!findShape) return []
    const filteredShapes = shapes.value
      .filter(shape => !shape.deleted_at)
      .filter(shape => `${shape.floorplan_id}` == `${findShape.floorplan_id}`)
    return getRelatedShapes(shapeId, filteredShapes) as Defect[]
  }
  // floor별 정렬 결과 메모이즈 — shapes 변경당 층별 1회만 정렬 (호출자마다 재정렬하지 않음)
  const _sortedShapesByFloor = computed((): Map<string, Defect[]> => {
    const byFloor = new Map<string, Defect[]>()
    for (const shape of shapes.value) {
      if (shape.deleted_at) continue
      const floorId = `${shape.floorplan_id}`
      let list = byFloor.get(floorId)
      if (!list) {
        list = []
        byFloor.set(floorId, list)
      }
      list.push(shape)
    }
    const map = new Map<string, Defect[]>()
    for (const [floorId, list] of byFloor) {
      map.set(floorId, sortShapesWithChildren(list))
    }
    return map
  })

  const shapesComputed = computed(() => {
    return (floorBranchDatabaseId: string): Defect[] => {
      return currentFloorBranchShapes(floorBranchDatabaseId)
    }
  })

  const currentFloorBranchShapes = (floorBranchDatabaseId: string): Defect[] => {
    // 호출부의 in-place 변조로부터 맵 내부 배열 보호를 위해 복사본 반환
    return [...(_sortedShapesByFloor.value.get(`${floorBranchDatabaseId}`) ?? [])]
  }

  const getCurrentShape = (shapeId: string): Defect | undefined => {
    return shapes.value.find(shape => shape.localStringId!.toString() == shapeId)
  }

  // SQLite에서 전체 shape 수를 조회 (shapes.value에 의존하지 않음)
  async function getTotalShapeCount(): Promise<number> {
    if (!SQL.createdTables.has(storeKey)) return 0
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT COUNT(*) as count FROM ${storeKey} WHERE task_id = ? AND (deleted_at IS NULL OR deleted_at = '')`,
      [currentTaskDatabaseId.value],
    )
    return result.values?.[0]?.count ?? 0
  }

  // SQLite에서 현재 task의 모든 shape의 ID 정보만 조회 (collectDownloadUrls 전용)
  // 전체 컬럼이 아닌 최소 필드만 반환하므로 다른 용도로 사용 금지
  async function getAllShapeRefsFromDB(): Promise<
    Array<{ localStringId: string; previous_defect_id: string | null; floorplan_id: number }>
  > {
    if (!SQL.createdTables.has(storeKey)) return []
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT localStringId, previous_defect_id, floorplan_id FROM ${storeKey} WHERE task_id = ? AND (deleted_at IS NULL OR deleted_at = '')`,
      [currentTaskDatabaseId.value],
    )
    return (result.values ?? []) as Array<{
      localStringId: string
      previous_defect_id: string | null
      floorplan_id: number
    }>
  }

  // 과업 전체의 "도형 없는 전차" 후보를 경량 컬럼만 조회 (보고서 가져오기 미배치 집계·'전차 배치' 시트용)
  // shape_data 본문은 읽지 않고 유무만 판정한다. 최종 판정(현차사진·is_current)은 호출자가 isUnplacedPrevDefect 로.
  async function getUnplacedPrevCandidatesFromDB(): Promise<UnplacedPrevCandidateRow[]> {
    if (!SQL.createdTables.has(storeKey)) return []
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT id, localStringId, floorplan_id, previous_balloon_number, is_current,
              structural_component, classification, width, length, area, count, memo, sort_key
         FROM ${storeKey}
        WHERE task_id = ?
          AND (deleted_at IS NULL OR deleted_at = '')
          AND previous_defect_id = 0
          AND (parent_defect_id IS NULL OR parent_defect_id = 0 OR parent_defect_id = '')
          AND (template IS NULL OR template != 'canvas-brush')
          AND (shape_type = 'NO_SHAPE' OR shape_data IS NULL OR shape_data = '' OR shape_data = 'null')`,
      [currentTaskDatabaseId.value],
    )
    return (result.values ?? []) as UnplacedPrevCandidateRow[]
  }

  /**
   * 층 전환 시 해당 floorplan의 shape만 SQLite에서 로드하여 shapes.value에 세팅
   * @param yieldBudgetMs 있으면 행 가공을 이 예산마다 양보하며 한다(층 진입 중 한 프레임을 막지 않게)
   */
  async function loadFloorplanShapes(floorplanId: string, yieldBudgetMs?: number): Promise<void> {
    activeFloorplanId.value = floorplanId
    if (!SQL.createdTables.has(storeKey)) {
      shapes.value = []
      taskServerIdMap.value = new Map()
      return
    }
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT * FROM ${storeKey} WHERE floorplan_id = ? AND task_id = ?`,
      [floorplanId, currentTaskDatabaseId.value],
    )
    // 빠른 층 전환 시 이전 호출의 stale 결과 무시
    if (activeFloorplanId.value !== floorplanId) return
    if (yieldBudgetMs == null) {
      shapes.value = (result.values?.map((row: unknown) => parseRow(row)) ?? []) as Defect[]
    } else {
      // 층 진입 중 가공이 한 프레임을 막지 않게 예산마다 양보하고, 반응형 할당은 끝에 한 번
      const yieldIfDue = createYielder(yieldBudgetMs)
      const rows: Defect[] = []
      for (const row of result.values ?? []) {
        await yieldIfDue()
        rows.push(parseRow(row) as Defect)
      }
      if (activeFloorplanId.value !== floorplanId) return
      shapes.value = rows
    }
    // 다른 층 결함의 서버 id 매핑도 함께 최신화 (사진 매칭이 현재 층에 종속되지 않도록)
    await loadTaskServerIdMap()
  }

  // SQLite에서 해당 도면의 shape를 직접 조회 (shapes.value에 의존하지 않음)
  async function getFloorShapesFromDB(floorplanId: string): Promise<Defect[]> {
    if (!SQL.createdTables.has(storeKey)) return []
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT * FROM ${storeKey} WHERE floorplan_id = ? AND task_id = ? AND (deleted_at IS NULL OR deleted_at = '')`,
      [floorplanId, currentTaskDatabaseId.value],
    )
    return (result.values?.map((row: unknown) => parseRow(row)) ?? []) as Defect[]
  }

  /**
   * 긴 가공을 시간 예산마다 이벤트 루프에 양보하며 하는 도우미(층 진입 중 번호 쿼리 결과 가공이 한 프레임을 막지 않게).
   * budgetMs가 없으면 양보하지 않는다
   */
  function createYielder(budgetMs?: number): () => Promise<void> {
    let sliceStart = performance.now()
    return async () => {
      if (budgetMs == null || performance.now() - sliceStart < budgetMs) return
      await new Promise<void>(resolve => {
        const channel = new MessageChannel()
        channel.port1.onmessage = (): void => {
          channel.port1.close()
          resolve()
        }
        channel.port2.postMessage(null)
      })
      sliceStart = performance.now()
    }
  }

  // facility의 모든 층 shape를 1회 쿼리로 가져와 floorplan_id별 Map으로 그룹핑
  async function getShapesByFacilityFromDB(
    facilityDatabaseId: string,
    yieldBudgetMs?: number,
  ): Promise<Map<string, Defect[]>> {
    const result = new Map<string, Defect[]>()
    if (!SQL.createdTables.has(storeKey)) return result
    const floorStore = useFloorplanStore()
    const floorplans = floorStore.getFloorplans(facilityDatabaseId)
    if (!floorplans || floorplans.length === 0) return result
    const floorIds = floorplans.map(f => f.id.toString())
    const placeholders = floorIds.map(() => '?').join(',')
    const db = await SQL.getDatabase()
    const queryResult = await db.query(
      `SELECT * FROM ${storeKey} WHERE floorplan_id IN (${placeholders}) AND task_id = ? AND (deleted_at IS NULL OR deleted_at = '')`,
      [...floorIds, currentTaskDatabaseId.value],
    )
    let rows: Defect[]
    if (yieldBudgetMs == null) {
      rows = (queryResult.values?.map((row: unknown) => parseRow(row)) ?? []) as Defect[]
    } else {
      const yieldIfDue = createYielder(yieldBudgetMs)
      rows = []
      for (const row of queryResult.values ?? []) {
        await yieldIfDue()
        rows.push(parseRow(row) as Defect)
      }
    }
    for (const row of rows) {
      const fid = row.floorplan_id.toString()
      let list = result.get(fid)
      if (!list) {
        list = []
        result.set(fid, list)
      }
      list.push(row)
    }
    return result
  }

  // 정렬 없이 해당 도면의 shape만 필터 (URL 수집 등 순서 무관 용도)
  const getFloorShapesUnsorted = (floorBranchDatabaseId: string): Defect[] => {
    return shapes.value.filter(
      shape => !shape.deleted_at && `${shape.floorplan_id}` == `${floorBranchDatabaseId}`,
    )
  }

  const getSortedShapes = (floorBranchDatabaseId: string): Defect[] => {
    return currentFloorBranchShapes(floorBranchDatabaseId)
  }

  const getShapesForReadOnly = (floorBranchDatabaseId: string): Defect[] => {
    const shapes = currentFloorBranchShapes(floorBranchDatabaseId)
    return shapes.map(shape => {
      return {
        id: shape.id,
        classification: shape.classification,
        structural_component: shape.structural_component,
        width: shape.width,
        length: shape.length,
        area: shape.area,
        count: shape.count,
        color: shape.color,
        repair_plan: shape.repair_plan,
        cause_estimate: shape.cause_estimate,
      } as Defect
    })
  }

  const notUploadedShapes = computed(() => {
    return shapes.value.filter(item => item.modifiedFromLocal)
  })
  const currentTaskDatabaseId = ref<string>('')
  async function uploadNotUploaded(): Promise<void> {
    const modified = await getAllModifiedData()
    if (modified.length > 0) {
      await requestUpdate(modified)
    }
  }
  async function individualSetLocalModified(
    localStringId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    keyValues: { key: string; value: any }[] = [],
  ): Promise<void> {
    // SQLite 데이터베이스 업데이트
    const db = await SQL.getDatabase()
    let sql = `UPDATE ${storeKey} SET `
    for (let i = 0; i < keyValues.length; i++) {
      const keyValue = keyValues[i]
      sql += `${keyValue.key} = ?`
      // 마지막 항목이 아니면 콤마 추가
      if (i < keyValues.length - 1) {
        sql += ', '
      }
    }
    sql += ' WHERE localStringId = ?'
    // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
    await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localStringId])

    // 이벤트 발행 (리스너 완료 대기)
    await SQLiteEventBus.getInstance().emitUpdate('defects', 'update', [localStringId])
  }

  /**
   * SQLite 이벤트 리스너 설정
   * @param taskId - 현재 task ID
   */
  function setupSQLiteListener(taskId: string): void {
    // 기존 리스너 해제 (중복 등록 방지)
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('defects', currentListener)
    }

    currentListener = async (event: SQLiteUpdateEvent): Promise<void> => {
      try {
        // 테이블이 아직 생성되지 않은 경우 스킵
        if (!SQL.createdTables.has(storeKey)) return
        // 활성 floorplan이 없으면 shapes.value를 갱신할 필요 없음
        if (!activeFloorplanId.value) return
        // 스냅샷: await 사이에 층 전환이 발생할 수 있으므로 진입 시 값 캡처
        const snapshotFloorplanId = activeFloorplanId.value

        // 방금 쓴 행이 실려 왔으면 재조회 없이 반영 (저장 1회당 브리지 왕복 1회 절약)
        const carriedRows = event.type !== 'bulk' && event.ids.length > 0 ? event.rows : undefined
        const db = carriedRows ? null : await SQL.getDatabase()

        // await 이후 층이 바뀌었으면 stale 이벤트 무시
        if (activeFloorplanId.value !== snapshotFloorplanId) return

        if (!db) {
          // SELECT ... WHERE localStringId IN (ids) AND task_id = ? 와 같은 결과를 만든다
          const fetchedById = new Map<string, Defect>()
          for (const row of carriedRows!) {
            if (`${row.task_id}` !== `${taskId}`) continue
            const item = parseRow(row) as Defect
            if (item.localStringId) fetchedById.set(item.localStringId, item)
          }
          applyFetchedShapes(event.ids, fetchedById, snapshotFloorplanId)
        } else if (event.type === 'bulk' || event.ids.length === 0) {
          // 대량 업데이트: 현재 활성 floorplan의 shape만 로드
          const localData = await db.query(
            `SELECT * FROM ${storeKey} WHERE floorplan_id = ? AND task_id = ?`,
            [snapshotFloorplanId, taskId],
          )
          if (activeFloorplanId.value !== snapshotFloorplanId) return
          shapes.value = localData.values?.map((row: unknown) => parseRow(row)) as Defect[]
          syncBalloonPositionsToCanvas(shapes.value)
          await loadTaskServerIdMap()
        } else {
          // 개별 업데이트: 대상 전체를 단일 쿼리로 조회 후 동기 일괄 반영 (플러시 1회)
          const CHUNK_SIZE = 500
          const fetchedById = new Map<string, Defect>()
          for (let i = 0; i < event.ids.length; i += CHUNK_SIZE) {
            const chunk = event.ids.slice(i, i + CHUNK_SIZE)
            const placeholders = chunk.map(() => '?').join(',')
            const result = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId IN (${placeholders}) AND task_id = ?`,
              [...chunk, taskId],
            )
            for (const row of result.values ?? []) {
              const item = parseRow(row) as Defect
              fetchedById.set(item.localStringId, item)
            }
          }
          applyFetchedShapes(event.ids, fetchedById, snapshotFloorplanId)
        }
      } catch (error) {
        console.error('[ShapeStore] Failed to process SQLite event:', error)
      }
    }

    SQLiteEventBus.getInstance().onTableUpdated('defects', currentListener)
  }

  /**
   * 개별 업데이트 이벤트로 읽은 행을 store 에 동기 일괄 반영 (플러시 1회)
   * @param ids 이벤트 ids (반영 순서 — 신규 push 상대 순서 보존)
   * @param fetchedById SQLite 에서 읽었거나 이벤트에 실려 온 행(parseRow 결과)
   * @param snapshotFloorplanId 리스너 진입 시 활성 층
   */
  function applyFetchedShapes(
    ids: string[],
    fetchedById: Map<string, Defect>,
    snapshotFloorplanId: string,
  ): void {
    // 서버 id 매핑은 층과 무관하므로 층 전환 여부와 상관없이 반영
    updateTaskServerIdMap([...fetchedById.values()])
    // 조회(await) 완료 후 층 전환 여부 1회 확인 — 이후 반영은 전부 동기
    if (activeFloorplanId.value !== snapshotFloorplanId) return

    const indexById = new Map<string, number>()
    for (let i = 0; i < shapes.value.length; i++) {
      indexById.set(shapes.value[i].localStringId, i)
    }
    for (const id of ids) {
      const updatedItem = fetchedById.get(id)
      if (!updatedItem) continue
      const index = indexById.get(id)
      if (index !== undefined) {
        // 기존 항목 업데이트 (deleted_at 포함 - restore를 위해 메모리에 유지)
        shapes.value[index] = updatedItem
      } else if (String(updatedItem.floorplan_id) === snapshotFloorplanId) {
        // 현재 활성 층의 shape만 추가 (다른 층의 shape가 섞이는 것 방지)
        shapes.value.push(updatedItem)
        // event.ids에 같은 id가 중복돼도 재-push되지 않도록 인덱스 등록
        indexById.set(id, shapes.value.length - 1)
      }
    }
    syncBalloonPositionsToCanvas([...fetchedById.values()])
  }

  /**
   * 병합·서버 응답 채택으로 SQLite의 번호풍선 위치가 캔버스와 달라졌으면 캔버스에 반영한다.
   * 반영하지 않으면 사용자는 옮긴 위치를 계속 보다가 층 재진입 때 되돌아간 것을 보게 된다(자가 복구 불가).
   * 로컬 수정 row(modifiedFromLocal=1)는 캔버스가 원본이므로 건드리지 않는다.
   * @param items SQLite에서 다시 읽은 row 목록
   */
  function syncBalloonPositionsToCanvas(items: Defect[]): void {
    const canvasStore = useCanvasStore()
    if (!canvasStore.getCanvasEngine()) return
    for (const item of items) {
      if (item.modifiedFromLocal || item.deleted_at) continue
      const balloonPoint = item.shape_data?.balloonPoint
      if (!balloonPoint) continue
      try {
        const entity = canvasStore.getEntityById(item.localStringId)
        if (!entity || entity.isLine() || entity.isBalloonDragging()) continue
        // 비선형 도형의 export balloonPoint는 풍선 head 좌표 그대로이므로 전체 export 없이 직접 읽는다
        const current = entity.getBalloonPosition()
        if (!current) continue
        if (
          Math.abs(current.x - balloonPoint.x) < 0.001 &&
          Math.abs(current.y - balloonPoint.y) < 0.001
        ) {
          continue
        }
        entity.moveBallon({ x: balloonPoint.x, y: balloonPoint.y })
        entity.invalidateCache()
      } catch (error) {
        console.warn('[ShapeStore] 풍선 위치 캔버스 반영 실패:', error)
      }
    }
  }
  async function getAllModifiedData(): Promise<Defect[]> {
    const db = await SQL.getDatabase()
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE modifiedFromLocal = 1`)
    return localData.values?.map((row: unknown) => parseRow(row)) as Defect[]
  }
  async function getServerData(taskId: string): Promise<Defect[]> {
    if (!taskId) return []
    try {
      const data: Defect[] = await APIServer.getShapeListByTaskId(Number(taskId))
      return data
        .filter(item => !item.deleted_at)
        .map(item => {
          return {
            ...item,
            localStringId: item.id.toString(),
            modifiedFromLocal: false,
          } as Defect
        })
    } catch (error) {
      console.error('Error getting server:', error)
      throw error
    }
  }

  const addApiQueue = async (itemsToProcess: { item: Defect; itemId: string }[]): Promise<void> => {
    if (itemsToProcess.length === 0) return

    const batchUpdates: Defect[] = []
    const batchDeletes: Defect[] = []

    for (const { item } of itemsToProcess) {
      if (!item.modifiedFromLocal) continue
      // 서버 검증 거절 표식 행: 다시 보내도 같은 거절이므로 사용자가 다시 저장할 때까지 job을 만들지 않는다
      if (isSyncRejectedActive(item)) {
        console.warn(`[Shape] 서버 거절 표식 행 동기화 보류: ${item.localStringId}`)
        continue
      }

      const isNew = String(item.id).includes('_need_to_upload')
      const isRestore = !isNew && !item.deleted_at && item.fields_updated_at?.need_restore
      const isDeleted =
        item.deleted_at &&
        !item.fields_updated_at?.need_restore &&
        !!item.fields_updated_at?.deleted_at

      // 로컬에서만 생성 후 삭제된 항목: 서버 호출 불필요
      if (isNew && isDeleted) {
        await individualSetLocalModified(item.localStringId!, [
          { key: 'modifiedFromLocal', value: false },
        ])
        continue
      }

      if (isNew || isRestore) {
        // 부모 생성이 서버 거절로 멈춘 자식 결함: job을 만들지 않는다 (부모 생성 시 onIdMap이 재투입)
        if (isNew && isParentCreateRejected(item)) {
          console.warn(`[Shape] 부모 결함 서버 거절로 동기화 보류: ${item.localStringId}`)
          syncQueue.markDependencyBlocked(String(item.parent_defect_id))
          continue
        }
        // create/restore는 의존성·개별 콜백 때문에 개별 job 유지
        addShapeJobToQueue(item)
      } else if (isDeleted) {
        batchDeletes.push(item)
      } else {
        batchUpdates.push(item)
      }
    }

    if (batchUpdates.length > 0) addBatchUpdateJobToQueue(batchUpdates)
    if (batchDeletes.length > 0) addBatchDeleteJobToQueue(batchDeletes)
  }

  // 부모(parent_defect_id가 로컬 id)가 서버 미생성 + 거절 표식인지 (메모리 기준 — 자식은 부모와 같은 층.
  // 못 찾으면 false로 두고 execute가 SQLite로 1회 판정해 종료시킨다)
  function isParentCreateRejected(item: Defect): boolean {
    if (typeof item.parent_defect_id !== 'string' || !item.parent_defect_id) return false
    const parent = shapeByKey.value.get(item.parent_defect_id)
    return (
      !!parent &&
      !!parent.modifiedFromLocal &&
      String(parent.id).includes('_need_to_upload') &&
      isSyncRejectedActive(parent)
    )
  }

  /**
   * 부모 결함이 서버에 생성된 뒤, 부모 거절 동안 종료·생략된 자식 결함 생성 job을 다시 넣는다
   * (큐에서 제거된 job이라 wakeFailedJobs로는 살아나지 않는다)
   * @param parentLocalStringId 생성된 부모 결함의 localStringId
   */
  async function requeueChildShapes(parentLocalStringId: string): Promise<void> {
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT * FROM ${storeKey} WHERE parent_defect_id = ? AND modifiedFromLocal = 1`,
      [parentLocalStringId],
    )
    const children = (result.values ?? [])
      .map((row: unknown) => parseRow(row) as Defect)
      .filter(
        (child: Defect) =>
          String(child.id).includes('_need_to_upload') &&
          !child.deleted_at &&
          !isSyncRejectedActive(child) &&
          !hasInFlightJob(child.localStringId),
      )
    if (children.length === 0) return
    console.info(
      `[Shape] 부모 결함 생성 → 자식 결함 ${children.length}건 재투입: ${parentLocalStringId}`,
    )
    for (const child of children) addShapeJobToQueue(child)
  }

  // 배치 작업의 404/409 에러 시 개별 job으로 폴백
  // 서버의 checkAccessPolicy는 배치 내 하나라도 soft-deleted면 전체 404를 반환하므로,
  // 개별 job으로 재시도하여 문제 없는 항목은 정상 처리되도록 한다.
  // 400/422(검증 거절)는 묶음 전부가 거절된 경우다(일부만이면 서버가 나머지를 반영하고 200 + rejected로 응답)
  // — 개별 job으로 쪼개 문제 행만 handleSyncRejected(값 정리·재전송, 그래도 거절이면 삭제·되돌림)로 보낸다.
  function fallbackToIndividualJobs(data: Defect[], errorCode: number | undefined): boolean {
    if (errorCode !== 404 && errorCode !== 409 && !isValidationRejectStatus(errorCode)) return false
    console.warn(`[Shape] Batch got ${errorCode} — falling back to ${data.length} individual jobs`)
    for (const item of data) {
      addShapeJobToQueue(item)
    }
    return true
  }

  // 묶음 수정이 통째로 검증 거절(400/422)됐을 때 서버가 알려준 거절 항목은 바로 handleSyncRejected로 보낸다
  // — 개별 job으로 같은 값을 다시 보내면 거절이 한 번 더 나고 서버 거절 기록도 두 번 남는다.
  // 거절 목록에 없는 항목(실행 직전 이미 저장돼 빠진 항목 등)만 개별 job으로 넘긴다.
  async function handleBatchRejected(
    error: unknown,
    data: Defect[],
    errorCode: number | undefined,
  ): Promise<boolean> {
    if (!isValidationRejectStatus(errorCode) || !(error instanceof ApiError)) return false
    const rejectedById = new Map<string, string>()
    for (const r of (error.rejected ?? []) as DefectRejectedItem[]) {
      if (r?.id != null) rejectedById.set(String(r.id), r.message)
    }
    if (rejectedById.size === 0) return false
    const rest: Defect[] = []
    for (const item of data) {
      const rejectedMessage = rejectedById.get(String(item.id))
      if (rejectedMessage === undefined) {
        rest.push(item)
        continue
      }
      await handleSyncRejected(
        item.localStringId,
        new Error(rejectedMessage),
        errorCode!,
        item.fields_updated_at,
      )
    }
    if (rest.length > 0) fallbackToIndividualJobs(rest, errorCode)
    return true
  }

  // 시작 전 수정 배치 job에 합칠 때의 항목 상한 (요청 본문이 과하게 커지지 않도록)
  const MAX_MERGED_UPDATE_BATCH = 100
  const UPDATE_BATCH_PREFIX = 'defects-update-batch-'

  // 여러 Defect update를 하나의 배치 작업으로 묶어 SyncQueue에 추가
  // 아직 시작 전인 수정 배치 job이 큐 끝에 있으면 새 job 대신 거기에 합친다 (연속 드래그마다 job·PUT이 쌓이지 않도록).
  // 같은 결함은 최신 항목으로 교체 — execute는 실행 직전 SQLite를 다시 읽고, onSuccess는 최신 항목의 fua와 비교한다.
  function addBatchUpdateJobToQueue(items: Defect[]): void {
    const merged = syncQueue.mergeIntoLatestPendingBatch<Defect[]>(
      ['defects', 'defects-batch'],
      UPDATE_BATCH_PREFIX,
      current => {
        const byId = new Map<string, Defect>()
        for (const d of current) byId.set(String(d.localStringId), d)
        for (const d of items) byId.set(String(d.localStringId), d)
        return byId.size > MAX_MERGED_UPDATE_BATCH ? null : [...byId.values()]
      },
    )
    if (merged) {
      console.info(`[Shape] 시작 전 수정 배치 job에 ${items.length}건 병합`)
      return
    }
    syncQueue.addJob({
      localStringId: `${UPDATE_BATCH_PREFIX}${getUnixtime()}-${getRandomString(4, false)}`,
      data: items,
      priority: JobPriority.NORMAL,
      maxRetries: 5,
      tag: 'defects-batch',
      execute: async (
        batchData: Defect[],
      ): Promise<{ rows: Defect[]; rejected: DefectRejectedItem[] }> => {
        // 실행 직전 SQLite에서 최신 데이터 재조회 (stale data 방지)
        const db = await SQL.getDatabase()
        const ids = batchData.map(d => d.localStringId)
        const placeholders = ids.map(() => '?').join(',')
        const freshRows = await db.query(
          `SELECT * FROM defects WHERE localStringId IN (${placeholders})`,
          ids,
        )
        const freshMap = new Map<string, Defect>()
        for (const row of freshRows.values ?? []) {
          const parsed = parseRow(row) as Defect
          freshMap.set(String(parsed.localStringId), parsed)
        }

        const payload: Defect[] = []
        for (const d of batchData) {
          const fresh = freshMap.get(d.localStringId)
          if (!fresh || !fresh.modifiedFromLocal) continue
          const shapeData = {
            id: Number(fresh.id),
            ..._.pick(fresh, UPDATE_SHAPE_KEYS),
            previous_balloon_number: fresh.previous_balloon_number ?? '',
            fields_updated_at: buildUploadFieldsUpdatedAt(fresh),
          } as Defect
          payload.push(Merge.returnHasValue(shapeData) as Defect)
        }
        if (payload.length === 0) return { rows: [], rejected: [] }
        // 서버는 검증에 걸린 항목만 빼고 반영한 뒤 200으로 응답한다 — 거절 목록은 onSuccess에서 항목별로 처리
        return await APIServer.updateShapesWithRejected(payload)
      },
      onSuccess: async (
        response: { rows: Defect[]; rejected: DefectRejectedItem[] },
        data: Defect[],
      ) => {
        const apiResults = response.rows
        // 서버 id → 거절 사유 (거절 항목은 응답에 없어 아래 채택 루프가 "저장됨"으로 처리하던 것을 막는다)
        const rejectedById = new Map<string, string>()
        for (const r of response.rejected) {
          if (r.id != null) rejectedById.set(String(r.id), r.message)
        }
        const db = await SQL.getDatabase()
        const localStringIds = data.map(d => d.localStringId)
        const placeholders = localStringIds.map(() => '?').join(',')
        const rows = await db.query(
          `SELECT * FROM defects WHERE localStringId IN (${placeholders})`,
          localStringIds,
        )
        // localStringId → 현재 SQLite row (fields_updated_at 비교·응답 채택 기준)
        const currentRowMap = new Map<string, Record<string, unknown>>()
        for (const row of rows.values ?? []) {
          currentRowMap.set(String(row.localStringId), parseRow(row) as Record<string, unknown>)
        }
        // 서버 응답을 id → result 로 매핑 (응답 SQLite 반영용)
        const resultMap = new Map<string, Partial<Defect>>()
        if (Array.isArray(apiResults)) {
          for (const r of apiResults as Partial<Defect>[]) {
            if (r && r.id != null) resultMap.set(String(r.id), r)
          }
        }
        // undo/redo 동안 변경된 항목은 skip, 나머지만 modifiedFromLocal=false 처리
        for (const item of data) {
          const rejectedMessage = rejectedById.get(String(item.id))
          if (rejectedMessage !== undefined) {
            await handleSyncRejected(
              item.localStringId,
              new Error(rejectedMessage),
              400,
              item.fields_updated_at,
            )
            continue
          }
          // ⚠️ JSON.stringify 비교는 키 순서에 의존해 항상 false 일치 → _.isEqual 깊은 비교
          // null/빈값도 "비어 있음"으로 정규화해 비교 (개별 job과 동일) — 신규 결함(fua 없음)에
          // 전송 중 수정이 끼어든 경우를 놓치고 서버 응답으로 되쓰는 것 방지
          const currentRow = currentRowMap.get(item.localStringId)
          const currentObj = normalizeFuaObject(currentRow?.fields_updated_at)
          const jobObj = normalizeFuaObject(item.fields_updated_at)
          if (!_.isEqual(currentObj, jobObj)) {
            continue
          }
          // 서버 응답을 SQLite에 반영 (색상 강제·스탬프 가드로 거부된 필드 포함 전 필드 채택)
          await individualSetLocalModified(
            item.localStringId,
            buildResponseAdoptUpdates(resultMap.get(String(item.id)), currentRow),
          )
        }
      },
      onError: async (error, data: Defect[], errorCode?: number) => {
        if (await handleBatchRejected(error, data, errorCode)) return
        if (fallbackToIndividualJobs(data, errorCode)) return
        console.error(`[Shape] Batch update with ${data.length} items FAILED.`, error)
      },
      onIdMap: undefined,
    })
  }

  // 여러 Defect delete를 하나의 배치 작업으로 묶어 SyncQueue에 추가
  function addBatchDeleteJobToQueue(items: Defect[]): void {
    syncQueue.addJob({
      localStringId: `defects-delete-batch-${getUnixtime()}-${getRandomString(4, false)}`,
      data: items,
      priority: JobPriority.NORMAL,
      maxRetries: 5,
      tag: 'defects-batch',
      execute: async (batchData: Defect[]): Promise<Defect[]> => {
        // 실행 직전 SQLite에서 최신 데이터 재조회 (이미 동기화된 항목 제외)
        const db = await SQL.getDatabase()
        const localIds = batchData.map(d => d.localStringId)
        const placeholders = localIds.map(() => '?').join(',')
        const freshRows = await db.query(
          `SELECT localStringId, id, modifiedFromLocal FROM defects WHERE localStringId IN (${placeholders})`,
          localIds,
        )
        const freshMap = new Map<string, { id: number | string; modifiedFromLocal: boolean }>()
        for (const row of freshRows.values ?? []) {
          freshMap.set(
            String(row.localStringId),
            row as { id: number | string; modifiedFromLocal: boolean },
          )
        }

        const deletePayload: { id: number }[] = []
        const itemsToSqlite: Defect[] = []
        for (const d of batchData) {
          const fresh = freshMap.get(d.localStringId)
          if (!fresh || !fresh.modifiedFromLocal) continue
          deletePayload.push({ id: Number(fresh.id) })
          itemsToSqlite.push(d)
        }
        if (deletePayload.length === 0) return batchData
        await APIServer.deleteShape(deletePayload)
        await onlySetSqlite(itemsToSqlite)
        return batchData
      },
      onSuccess: async (_result, data: Defect[]) => {
        for (const item of data) {
          await individualSetLocalModified(item.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
        }
      },
      onError: (error, data: Defect[], errorCode?: number) => {
        if (fallbackToIndividualJobs(data, errorCode)) return
        console.error(`[Shape] Batch delete with ${data.length} items FAILED.`, error)
      },
      onIdMap: undefined,
    })
  }

  const requestUpdate = async (datas: Defect[]): Promise<void> => {
    // 다시 저장하면 서버 거절 표식을 지워 재전송한다 (고쳐졌으면 성공, 아니면 다시 거절 표식)
    const sortedDatas = sortByKeys(datas.map(clearSyncRejection))
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
      sortedDatas as unknown as Defect[],
      storeKey,
    )
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    await addApiQueue(itemsToProcess as { item: Defect; itemId: string }[])
  }

  /**
   * Defect 동기화 작업을 SyncQueue에 추가하는 헬퍼 함수
   * @param item 동기화할 Defect 데이터
   */
  async function addShapeJobToQueue(item: Defect): Promise<void> {
    const isNew = String(item.id).includes('_need_to_upload')
    const isRestore = !isNew && !item.deleted_at && item.fields_updated_at?.need_restore
    // deleted_at 값만으로 삭제 의도를 판단하면, mergeBothData / 404·409 핸들러 등이
    // 자동으로 박은 deleted_at 플래그까지 서버 DELETE로 둔갑할 수 있다.
    // 정상 deleteShape 경로는 fields_updated_at.deleted_at 까지 함께 stamp 하므로
    // 이 두 값이 모두 있을 때만 진짜 삭제 의도로 간주한다.
    const isDeleted =
      item.deleted_at &&
      !item.fields_updated_at?.need_restore &&
      !!item.fields_updated_at?.deleted_at
    // 작업 유형에 따라 API 호출 함수(execute)를 결정합니다.
    let executeCallback: (data: Defect) => Promise<void | Defect>
    if (isDeleted) {
      // --- 삭제 작업 ---
      // 서버 ID가 없고 진행 중인 업로드 job도 없는 항목(로컬 생성 후 바로 삭제)은
      // 서버에 알릴 필요가 없습니다.
      if (isNew && !hasInFlightJob(item.localStringId)) {
        await individualSetLocalModified(item.localStringId!, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return
      }
      executeCallback = async (data): Promise<Defect> => {
        // 실행 시점 기준 서버 미생성 항목(업로드가 삭제로 무효화된 경우): 서버 호출 없이 종료
        if (String(data.id).includes('_need_to_upload')) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }
        await APIServer.deleteShape([{ id: Number(data.id) }])
        await onlySetSqlite([data])
        return data
      }
    } else if (isNew) {
      // --- 생성 작업 ---
      executeCallback = async (data): Promise<Defect> => {
        // 업로드 전에 로컬에서 삭제된 항목: 서버에 생성하지 않고 동기화 종료
        if (data.deleted_at) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }
        // 이미 서버에 생성 완료된 shape인지 확인 (CREATE 처리 중 UpdateValue가 호출되어 pendingQueue에서 재실행된 경우)
        // data는 실행 직전 SQLite에서 재조회된 최신 row이므로 data.id를 우선 신뢰하고,
        // (merge의 content 매칭이 SQLite에만 서버 id를 반영했고 store 메모리가 아직 낡은 경우 대비)
        // 보조로 store 메모리도 확인한다.
        const currentShape = shapes.value.find(s => s.localStringId === data.localStringId)
        const syncedId = !String(data.id).includes('_need_to_upload')
          ? data.id
          : currentShape && !String(currentShape.id).includes('_need_to_upload')
            ? currentShape.id
            : null
        if (syncedId != null) {
          // 이미 서버 ID가 있으면 UPDATE로 전환하여 중복 CREATE(409) 방지
          const shapeData = {
            id: Number(syncedId),
            ..._.pick(data, UPDATE_SHAPE_KEYS),
            previous_balloon_number: data.previous_balloon_number ?? '',
          } as Defect
          const [result] = await APIServer.updateShapes([Merge.returnHasValue(shapeData) as Defect])
          return result
        }

        // 생성 본문은 fields_updated_at을 통째로 보내므로 로컬 전용 거절·수리 표식을 뺀다
        const pickedData = clearSyncRejection(_.pick(data, ADD_SHAPE_KEYS))

        // parent_defect_id 처리 - localStringId인 경우 실제 서버 ID로 변환
        if (pickedData.parent_defect_id) {
          if (typeof pickedData.parent_defect_id === 'string') {
            // localStringId인 경우 실제 서버 ID 찾기
            const parentServerId = await syncQueue.findShapeId(pickedData.parent_defect_id)
            if (String(parentServerId).includes('_need_to_upload')) {
              // 부모 생성이 서버 거절로 멈췄으면 기다려도 풀리지 않는다 — job 종료(부모 생성 시 onIdMap이 재투입)
              if (await syncQueue.isDependencyRejected(pickedData.parent_defect_id)) {
                throw new DependencyRejectedError(
                  `Dependent parent shape ${pickedData.parent_defect_id} was rejected by server.`,
                )
              }
              throw new DependencyNotReadyError(
                `Dependent parent shape ${pickedData.parent_defect_id} is not synced yet.`,
              )
            }
            pickedData.parent_defect_id = Number(parentServerId)
          }
          // 이미 숫자인 경우는 그대로 사용
        }

        const shapeData = { ...pickedData } as Defect

        const [result] = await APIServer.addShape([Merge.returnHasValue(shapeData) as Defect])
        // onlySetSqlite는 onIdMap에서 호출됩니다 (여기서는 제거)
        return result
      }
    } else if (isRestore) {
      // undo로 복원된 항목은 따로처리
      // restore api를 탈 수 있는 요소는 모두 동기화 완료된 요소임.
      executeCallback = async (data): Promise<void> => {
        const shapeId = data.id
        await APIServer.restoreShape([{ id: Number(shapeId) }])
        const newData = Merge.returnHasValue(clearSyncRejection(data)) as Defect
        const [_result] = await APIServer.updateShapes([{ ...newData, id: Number(shapeId) }])

        await individualSetLocalModified(data.localStringId, [{ key: 'deleted_at', value: null }])
      }
    } else {
      // --- 업데이트 작업 ---
      executeCallback = async (data): Promise<Defect> => {
        const shapeData = {
          id: Number(data.id),
          ..._.pick(data, UPDATE_SHAPE_KEYS),
          previous_balloon_number: data.previous_balloon_number ?? '',
          fields_updated_at: buildUploadFieldsUpdatedAt(data),
        } as Defect
        const [result] = await APIServer.updateShapes([Merge.returnHasValue(shapeData) as Defect])
        return result
      }
    }

    // 이제 SyncQueue에 작업을 추가합니다.
    syncQueue.addJob({
      localStringId: item.localStringId, // Upsert 로직을 위한 고유 식별자
      data: item,
      priority: JobPriority.NORMAL,
      maxRetries: 5,
      tag: 'defects',
      // 실제 API를 호출하는 함수
      execute: executeCallback,

      // 작업 성공 시 실행될 로직
      onSuccess: async (apiResult, data) => {
        // 생성(CREATE)의 ID 매핑은 onIdMap에서 처리되고, 여기서는 모든 작업 공통으로
        // 동기화 완료(modifiedFromLocal=false) 처리를 한다.
        {
          // SQLite에서 현재 데이터를 조회하여 작업 시작 시점과 다른지 확인
          // (undo/redo로 인해 데이터가 변경되었을 수 있음)
          const db = await SQL.getDatabase()
          const result = await db.query('SELECT * FROM defects WHERE localStringId = ?', [
            data.localStringId,
          ])
          const currentRow = result.values?.[0]
            ? (parseRow(result.values[0]) as Record<string, unknown>)
            : undefined

          // fields_updated_at 비교: 작업 시작 시점과 현재가 다르면 데이터가 변경된 것
          // (undo/redo, 패널 자동입력 등) — null/빈값도 "비어 있음"으로 정규화해 비교해야
          // 신규 결함(생성 시점 fua 없음)에 자동입력이 끼어든 경우를 놓치지 않는다
          // ⚠️ JSON.stringify 비교는 키 순서에 의존해서 실제 동일한 객체도 다르게 판정될 수 있다 →
          // _.isEqual로 깊은 비교
          const normalizeFieldsUpdatedAt = (raw: unknown): Record<string, unknown> => {
            if (!raw) return {}
            if (typeof raw === 'string') {
              try {
                return JSON.parse(raw)
              } catch {
                return {}
              }
            }
            return raw as Record<string, unknown>
          }
          const currentObj = normalizeFieldsUpdatedAt(currentRow?.fields_updated_at)
          const jobObj = normalizeFieldsUpdatedAt(data.fields_updated_at)
          if (!_.isEqual(currentObj, jobObj)) {
            // 데이터가 변경됨 (undo/redo, 자동입력 등) - modifiedFromLocal 유지하여 다음 작업 실행
            console.info(
              `[SyncQueue] Shape ${data.localStringId} has been modified during sync. Skipping modifiedFromLocal reset.`,
            )
            return
          }

          // 서버 응답을 SQLite에 반영 (색상 강제·스탬프 가드로 거부된 필드 포함 전 필드 채택)
          // 이게 없으면 다음 동기화 시 잘못된 로컬 데이터로 서버를 다시 덮어쓸 위험.
          await individualSetLocalModified(
            data.localStringId,
            buildResponseAdoptUpdates(apiResult as Partial<Defect> | undefined, currentRow),
          )
        }
      },

      // 작업 실패 시 실행될 로직
      onError: async (error, data, errorCode) => {
        console.error(
          `[PhotoGuard] shape job failed id=${data.localStringId} code=${errorCode ?? 'none'} isNew=${isNew}: ${error instanceof Error ? error.message : String(error)}`,
        )
        const canvasStore = useCanvasStore()
        const eventLogStore = useDeviceEventLogStore()
        const canvasHandlerState: CanvasNotFoundHandler = canvasStore.canvasNotFoundHandler(
          data.localStringId,
          'app',
        )

        // 생성 중복 409: 서버에 같은 결함이 이미 존재 (응답 유실 후 재시도 등).
        // 로컬 row를 삭제하면 그 사이의 미전송 수정(번호풍선 이동 등)까지 함께 유실되고
        // 서버의 옛 상태로 복귀되므로, row를 dirty로 유지한 채 job만 제거한다.
        // 다음 동기화 사이클의 merge content 매칭이 서버 id를 붙이고 UPDATE로 재전송한다.
        if (errorCode === 409 && isNew) {
          console.warn(
            `[SyncQueue] Duplicate CREATE(409) for ${data.localStringId} - 로컬 유지, merge 재바인딩 대기`,
          )
          syncQueue.removeJobByLocalStringId(data.localStringId)
          return
        }

        // 409(중복) 또는 404(서버 삭제됨) 에러인 경우 공통 처리
        if (errorCode === 409 || errorCode === 404) {
          // 1. 로컬 DB 업데이트 (공통)
          await individualSetLocalModified(data.localStringId, [
            { key: 'deleted_at', value: Merge.getCurrentTime() },
            { key: 'modifiedFromLocal', value: false },
          ])

          // 2. SyncQueue에서 작업 제거 (공통)
          syncQueue.removeJobByLocalStringId(data.localStringId)
          await eventLogStore.deleteLogs(data.localStringId, Merge.getCurrentTime())
          // 3. 캔버스 및 캐시 정리 (공통)
          if (canvasHandlerState.canvasState === 'SETTED') {
            UTIL.deleteMultipleEntities([data.localStringId])
            UTIL.refreshCache()

            // 4. 피드백 메시지 처리 (차이점 분기)
            if (canvasHandlerState.shapeNum) {
              switch (errorCode) {
                case 409:
                  console.warn('중복된 DEFECT 데이터가 서버에 존재합니다. 로컬 삭제처리')
                  break
                case 404:
                  App.toastInstance.add(
                    'error',
                    `결함${canvasHandlerState.shapeNum}번이 서버에서 삭제되었습니다. 기기에서 삭제처리 됩니다.`,
                    6000,
                  )
                  break
                default:
                  break
              }
            }
          }
          return // 처리 완료 후 종료
        }

        // 서버 검증 거절: 재시도해도 같으므로 자동 수리(1회) 또는 거절 표식 후 job 종료
        if (isValidationRejectStatus(errorCode)) {
          await handleSyncRejected(data.localStringId, error, errorCode!, data.fields_updated_at)
          return
        }

        // 그 외 에러 처리
        console.error(`[SyncQueue] Defect job for ${data.localStringId} FAILED.`, error)
      },

      // ID 매핑 로직 (생성 작업에만 필요)
      // 서버 생성 필드(id, created_at, updated_at)만 반영하고 로컬 데이터는 절대 덮어쓰지 않음
      onIdMap: isNew
        ? async (localStringId, serverResult): Promise<void> => {
            const updates: { key: string; value: unknown }[] = [
              { key: 'id', value: serverResult.id },
              { key: 'created_at', value: serverResult.created_at },
              { key: 'updated_at', value: serverResult.updated_at },
            ]
            // 서버 응답의 facility_id로 로컬 SQLite 보정
            if (serverResult.facility_id) {
              updates.push({ key: 'facility_id', value: serverResult.facility_id })
            }
            await individualSetLocalModified(localStringId, updates)

            const shapeInStore = shapes.value.find(s => s.localStringId == localStringId)
            if (shapeInStore) {
              shapeInStore.id = serverResult.id
              shapeInStore.created_at = serverResult.created_at
              shapeInStore.updated_at = serverResult.updated_at
              if (serverResult.facility_id) {
                shapeInStore.facility_id = serverResult.facility_id
              }
            }

            // 결함 id 매핑 완료 → DependencyNotReady로 대기 중인 사진·자식 결함 job을 즉시 재시도
            // (30초 retry sweep 대기 없이 배지가 빨리 해소되도록)
            syncQueue.wakeFailedJobs('defectPhoto')
            syncQueue.wakeFailedJobs('defects')
            // 부모 거절 동안 종료·생략돼 큐에 없는 사진·자식 결함 job을 다시 넣는다
            // (막힌 적 있는 부모만 — 결함 생성마다 SQLite 조회를 늘리지 않도록)
            if (syncQueue.takeDependencyBlocked(localStringId)) {
              await useDefectPhotoStore().requeuePhotosOfShape(localStringId)
              await requeueChildShapes(localStringId)
            }
          }
        : undefined,
    })
  }
  /**
   * 서버 검증 거절(400/422) 처리.
   * 1) 처음 거절이면 값을 서버 규칙에 맞게 정리(빈 색상 → 기본색, 글자 수 초과 → 상한까지 자르기)해 1회 다시 보낸다.
   *    자르기 전 원본은 서버가 첫 거절 때 defect_sync_rejections에 남긴다.
   * 2) 정리할 게 없거나 정리 후에도 거절이면 — 서버 미생성 결함은 기기에서 삭제(사진·자식 결함 포함),
   *    서버에 있는 결함은 수정 내용을 버리고 서버 값으로 되돌린다. 처리 전 내용은 서버에 기록하고 사용자에게 알린다.
   * 2)를 끝내지 못하면 거절 표식을 남겨 재적재를 미룬다 — 서버 응답이 없는 실패(되돌릴 서버 값 조회 중 오프라인 등)는
   *    일시 표식(1분 뒤 주기 동기화가 다시 보냄, '저장 대기'로 셈), 그 밖은 6시간 표식('저장 실패'로 셈).
   * @param localStringId 거절된 결함
   * @param error 서버 오류 (메시지에 거절 필드가 담긴다, 예: "[0]: color")
   * @param status HTTP 상태 코드
   * @param jobFieldsUpdatedAt 거절된 job이 보낸 시점의 fields_updated_at (현재 행과 다르면 전송 중 수정된 것)
   */
  async function handleSyncRejected(
    localStringId: string,
    error: unknown,
    status: number,
    jobFieldsUpdatedAt: unknown,
  ): Promise<void> {
    const message = error instanceof Error ? error.message : String(error)
    const db = await SQL.getDatabase()
    const result = await db.query('SELECT * FROM defects WHERE localStringId = ?', [localStringId])
    const row = result.values?.[0] ? (parseRow(result.values[0]) as Defect) : undefined
    if (!row) return
    const fieldUpdates = normalizeFuaObject(row.fields_updated_at)
    // 전송 중 사용자가 다시 고친 행: 아래 처리(삭제·되돌림)가 새 편집을 지우지 않도록 손대지 않는다
    // (새 편집은 자기 job으로 다시 올라가고, 그것도 거절되면 그때 처리한다)
    // 거절·수리 표식은 편집이 아니다 — 같은 행의 job이 둘 실려 앞 job의 처리가 표식을 남긴 뒤 뒤 job이 거절되면
    // 표식 차이로 처리를 건너뛰어 행이 보류된 채 남았다
    const withoutMarkers = (fua: Record<string, unknown>): Record<string, unknown> =>
      _.omit(fua, [SYNC_REJECTED_KEY, SYNC_REPAIRED_KEY])
    if (
      !_.isEqual(
        withoutMarkers(fieldUpdates),
        withoutMarkers(normalizeFuaObject(jobFieldsUpdatedAt)),
      )
    ) {
      console.info(`[ShapeStore] 거절 결함이 전송 중 수정됨 — 처리 생략: ${localStringId}`)
      return
    }

    if (!fieldUpdates[SYNC_REPAIRED_KEY] && (await repairRejectedShape(row, fieldUpdates))) return

    console.error(
      `[ShapeStore] 서버 검증 거절(${status}) — 정리 후 재전송 불가·재거절: ${localStringId} ${message}`,
    )
    try {
      if (String(row.id).includes('_need_to_upload')) {
        await discardRejectedNewShape(row, message)
      } else {
        await revertRejectedShape(row, message)
      }
    } catch (handleError) {
      console.error(
        `[ShapeStore] 거절 결함 정리 실패 — 거절 표식으로 보류: ${localStringId}`,
        handleError,
      )
      await individualSetLocalModified(localStringId, [
        {
          key: 'fields_updated_at',
          value: {
            ...fieldUpdates,
            [SYNC_REJECTED_KEY]: {
              at: Merge.getCurrentTime(),
              status,
              message: message.slice(0, 200),
              // 서버 응답이 없는 실패(오프라인 등)는 연결되면 곧 다시 보내 처리를 마친다 — 6시간 보류하면 그동안
              // 기기에만 거절된 값이 남는다. 서버가 응답한 실패(5xx 등)는 반복 재전송을 피해 기본 유예를 둔다.
              ...(!(handleError instanceof ApiError) && { transient: true }),
            },
          },
        },
      ])
    }
  }

  /**
   * 거절된 결함 값을 서버 규칙에 맞게 고쳐 1회 다시 보낸다
   * @param row 거절된 결함
   * @param fieldUpdates row의 fields_updated_at (객체)
   * @returns 고칠 값이 있어 다시 보냈으면 true
   */
  async function repairRejectedShape(
    row: Defect,
    fieldUpdates: Record<string, unknown>,
  ): Promise<boolean> {
    const localStringId = row.localStringId
    const fixed: Record<string, unknown> = sanitizeDefectForServer(
      row as unknown as Record<string, unknown>,
    )
    let shapeData = row.shape_data as Record<string, unknown> | null | undefined
    const canvasStore = useCanvasStore()
    if (!row.color) {
      // 폴백 순서는 생성 시 빈 색상 방어(addShapeToHistory)와 같다. 색상 규칙(defect.ts)은 건드리지 않는다.
      shapeData = { ...(shapeData ?? {}) }
      const color =
        (typeof shapeData.color === 'string' && shapeData.color) ||
        canvasStore.getCanvasEngine()?.forCreateBalloonBorderColor ||
        getCurrentColor('red') ||
        '#FF0000'
      shapeData.color = color
      fixed.color = color
    }
    const fixedKeys = Object.keys(fixed)
    if (fixedKeys.length === 0) return false

    console.warn(
      `[ShapeStore] 서버 거절 값 정리 후 재전송(${fixedKeys.join(', ')}): ${localStringId}`,
    )
    let nextFieldUpdates: Record<string, unknown> = fieldUpdates
    for (const key of fixedKeys) nextFieldUpdates = Merge.setFieldUpdate(nextFieldUpdates, key)
    const updates: { key: string; value: unknown }[] = fixedKeys.map(key => ({
      key,
      value: fixed[key],
    }))
    if (fixed.color) updates.push({ key: 'shape_data', value: shapeData })
    const repairedFieldUpdates = {
      ...nextFieldUpdates,
      [SYNC_REPAIRED_KEY]: Merge.getCurrentTime(),
    }
    updates.push(
      { key: 'fields_updated_at', value: repairedFieldUpdates },
      { key: 'modifiedFromLocal', value: true },
    )
    await individualSetLocalModified(localStringId, updates)
    if (typeof fixed.color === 'string') {
      canvasStore.getEntityById(localStringId)?.changeColor(fixed.color)
    }
    // 실행 중인 이 job 뒤 대기 큐로 들어가고, 큐가 거절 job을 정리하며 바로 넘겨받는다
    await addShapeJobToQueue({
      ...row,
      ...fixed,
      shape_data: shapeData,
      fields_updated_at: repairedFieldUpdates,
    } as Defect)
    return true
  }

  // 거절 처리 기록 — 서버에 남기지 못해도 처리는 계속한다 (서버 검증 거절 자체는 서버가 따로 기록)
  async function recordRejectionHandled(log: DefectSyncRejectionLog): Promise<void> {
    try {
      await APIServer.addDefectSyncRejections([log])
    } catch (logError) {
      console.warn('[ShapeStore] 거절 결함 처리 기록 실패:', logError)
    }
  }

  /**
   * 서버에 만들어지지 못한 거절 결함을 기기에서 삭제한다 (달린 사진·자식 결함과 그 사진까지).
   * 자식·사진은 부모가 서버에 없어 올라간 적이 없다 — 서버 id가 있는 행은 건드리지 않는다.
   * @param row 거절된 결함
   * @param message 거절 사유
   */
  async function discardRejectedNewShape(row: Defect, message: string): Promise<void> {
    const db = await SQL.getDatabase()
    const children = (
      (
        await db.query(
          `SELECT * FROM ${storeKey} WHERE parent_defect_id = ? AND deleted_at IS NULL`,
          [row.localStringId],
        )
      ).values ?? []
    )
      .map((r: unknown) => parseRow(r) as Defect)
      .filter((child: Defect) => String(child.id).includes('_need_to_upload'))
    const defectIds = [row.localStringId, ...children.map((c: Defect) => c.localStringId)]
    const placeholders = defectIds.map(() => '?').join(',')
    const photos = (
      (
        await db.query(
          `SELECT * FROM defect_photos WHERE localShapeId IN (${placeholders}) AND deleted_at IS NULL`,
          defectIds,
        )
      ).values ?? []
    )
      .map((r: unknown) => parseRow(r) as DefectPhoto)
      .filter((photo: DefectPhoto) => String(photo.id).includes('_need_to_upload'))

    await recordRejectionHandled({
      action: 'deleted',
      payload: row as unknown as Record<string, unknown>,
      related: {
        children,
        photos: photos.map((photo: DefectPhoto) => ({
          localStringId: photo.localStringId,
          localShapeId: photo.localShapeId,
          url: String(photo.url ?? '').slice(0, 500),
          resourcePath: photo.resourcePath,
          is_main: photo.is_main,
          created_at: photo.created_at,
        })),
      },
      task_id: row.task_id,
      floorplan_id: row.floorplan_id,
      local_string_id: row.localStringId,
      message,
    })

    const deletedTime = Merge.getCurrentTime()
    const eventLogStore = useDeviceEventLogStore()
    for (const id of defectIds) {
      await db.run(
        `UPDATE ${storeKey} SET deleted_at = ?, modifiedFromLocal = 0 WHERE localStringId = ?`,
        [deletedTime, id],
      )
      syncQueue.removeJobByLocalStringId(id)
      syncQueue.takeDependencyBlocked(id)
      await eventLogStore.deleteLogs(id, deletedTime)
    }
    const photoIds = photos.map((photo: DefectPhoto) => photo.localStringId)
    for (const id of photoIds) {
      await db.run(
        'UPDATE defect_photos SET deleted_at = ?, modifiedFromLocal = 0 WHERE localStringId = ?',
        [deletedTime, id],
      )
      syncQueue.removeJobByLocalStringId(id)
      await eventLogStore.deleteLogs(id, deletedTime)
    }

    const canvasStore = useCanvasStore()
    const onCanvas = canvasStore.getCanvasEngine()
      ? defectIds.filter(id => !!canvasStore.getEntityById(id))
      : []
    if (onCanvas.length > 0) {
      UTIL.deleteMultipleEntities(onCanvas)
      UTIL.refreshCache()
    }
    await SQLiteEventBus.getInstance().emitUpdate(storeKey, 'update', defectIds)
    if (photoIds.length > 0) {
      await SQLiteEventBus.getInstance().emitUpdate('defect_photos', 'delete', photoIds)
    }
    noticeRejectionHandled('deleted', defectIds.length)
  }

  /**
   * 서버에 있는 결함의 거절된 수정을 버리고 서버 값으로 되돌린다
   * @param row 거절된 결함 (서버 id 있음)
   * @param message 거절 사유
   */
  async function revertRejectedShape(row: Defect, message: string): Promise<void> {
    const serverRows = await APIServer.getShape(String(row.id))
    const serverRow = serverRows?.[0]
    if (!serverRow || serverRow.deleted_at) {
      // 서버에서 이미 지워진 결함 — 404와 같은 처리(기기에서 삭제)로 넘긴다
      await discardDeletedOnServer(row)
      return
    }

    await recordRejectionHandled({
      action: 'reverted',
      payload: row as unknown as Record<string, unknown>,
      related: { server: serverRow as unknown as Record<string, unknown> },
      task_id: row.task_id,
      floorplan_id: row.floorplan_id,
      defect_id: row.id,
      local_string_id: row.localStringId,
      message,
    })

    syncQueue.removeJobByLocalStringId(row.localStringId)
    // 서버 응답 채택 규칙(빈 로컬 값 보존)과 달리 되돌림은 서버 값 그대로 — localRow 없이 전 필드 채택
    const updates = buildResponseAdoptUpdates(serverRow, undefined)
    updates.push(
      { key: 'previous_balloon_number', value: serverRow.previous_balloon_number ?? null },
      { key: 'deleted_at', value: null },
    )
    await individualSetLocalModified(row.localStringId, updates)

    const canvasStore = useCanvasStore()
    const engine = canvasStore.getCanvasEngine()
    if (engine && canvasStore.getEntityById(row.localStringId)) {
      const index = Number(canvasStore.getIndex(row.localStringId)) || 0
      UTIL.deleteMultipleEntities([row.localStringId])
      UTIL.createMultipleEntities(
        [{ ...row, ...serverRow, localStringId: row.localStringId } as Defect],
        [index],
      )
      updateShapes()
    }
    noticeRejectionHandled('reverted', 1)
  }

  // 되돌리려던 결함이 서버에서 이미 삭제됨 — 404 처리와 같이 기기에서도 삭제
  async function discardDeletedOnServer(row: Defect): Promise<void> {
    const deletedTime = Merge.getCurrentTime()
    await individualSetLocalModified(row.localStringId, [
      { key: 'deleted_at', value: deletedTime },
      { key: 'modifiedFromLocal', value: false },
    ])
    syncQueue.removeJobByLocalStringId(row.localStringId)
    await useDeviceEventLogStore().deleteLogs(row.localStringId, deletedTime)
    const canvasStore = useCanvasStore()
    if (canvasStore.getCanvasEngine() && canvasStore.getEntityById(row.localStringId)) {
      UTIL.deleteMultipleEntities([row.localStringId])
      UTIL.refreshCache()
    }
  }

  // 거절 결함 처리 안내 — 묶음 거절은 하나씩 처리되므로 잠깐 모아 1회 알린다
  const rejectionNotice = { deleted: 0, reverted: 0, timer: 0 as ReturnType<typeof setTimeout> | 0 }
  function noticeRejectionHandled(kind: 'deleted' | 'reverted', count: number): void {
    rejectionNotice[kind] += count
    if (rejectionNotice.timer) return
    rejectionNotice.timer = setTimeout(() => {
      const { deleted, reverted } = rejectionNotice
      rejectionNotice.deleted = 0
      rejectionNotice.reverted = 0
      rejectionNotice.timer = 0
      if (deleted > 0) {
        App.toastInstance.add(
          'error',
          `결함 ${deleted}건을 서버에 저장하지 못해 기기에서 삭제했습니다.`,
          6000,
        )
      }
      if (reverted > 0) {
        App.toastInstance.add(
          'error',
          `결함 ${reverted}건의 수정 내용을 서버에 저장하지 못해 이전 상태로 되돌렸습니다.`,
          6000,
        )
      }
    }, 1500)
  }

  async function onlySetSqlite(items: Defect[]): Promise<void> {
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
      items as unknown as Defect[],
      storeKey,
    )
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
  }

  async function initData(
    taskId: string,
    signal?: AbortSignal,
    skipServerSync = false,
  ): Promise<void> {
    currentTaskDatabaseId.value = taskId
    setupSQLiteListener(taskId)

    if (skipServerSync) return

    try {
      let mergedData = (await SQLite.getLocalData(storeKey, 'task_id', taskId, false)) as Defect[]
      if (signal?.aborted) return
      // 병합 스냅샷의 row별 스탬프 — 저장 직전 현재 row와 비교해 병합 창(GET~저장) 사이의
      // 사용자 수정(풍선 이동 등)을 스냅샷으로 되쓰지 않기 위한 기준
      const snapshotFieldUpdates = new Map<string, string>()
      for (const item of mergedData) {
        snapshotFieldUpdates.set(
          String(item.localStringId),
          normalizeFieldUpdates(item.fields_updated_at),
        )
      }
      // 병합 전 DB 행 값 — 병합 결과가 같은 행은 다시 쓰지 않는다
      const localSnapshot = await SQLite.snapshotRowsById(
        mergedData as unknown as Record<string, unknown>[],
      )

      let didMerge = false
      if ((await Network.getStatus()).connected) {
        const serverData = await getServerData(taskId)
        if (signal?.aborted) return

        await SQLite.checkTableExists(storeKey, serverData)
        const activeLocalData = mergedData.filter(item => !item.deleted_at)
        if (serverData.length > 0 || activeLocalData.length === 0) {
          mergedData = Merge.mergeBothData(mergedData, serverData, storeKey)
          didMerge = true
        }
      }

      if (signal?.aborted) return

      if (mergedData.length > 0) {
        if (didMerge) {
          const isAlreadySyncing = SyncManager.isTaskSyncing(taskId)
          if (!isAlreadySyncing) {
            const sortedDatas = sortByKeys(mergedData)
            const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
              sortedDatas as unknown as Defect[],
              storeKey,
            )
            const { items: changedItems, skipped } = await SQLite.skipUnchangedRows(
              itemsToProcess,
              storeKey,
              currentTaskDatabaseId.value,
              localSnapshot,
            )
            console.info(
              `[Shape] initData 병합 저장: ${changedItems.length}건 기록, ${skipped}건 변경 없음`,
            )
            // 병합 중 업로드 job이 교체한 row의 옛 스냅샷은 저장 제외 (유령 row 부활 방지)
            const safeItems = await filterStaleMergedItems(
              changedItems as { item: Defect; itemId: string }[],
              storeKey,
              taskId,
              storeKey,
              snapshotFieldUpdates,
            )
            await SQLite.updateSqliteOnly(
              safeItems,
              columns,
              currentTaskDatabaseId.value,
              storeKey,
              true,
              true,
            )
          }
        }
      }
      // 병합 저장은 이벤트를 발행하지 않으므로(skipEmit) 서버 id 매핑을 직접 최신화
      await loadTaskServerIdMap()
    } catch (e) {
      if (signal?.aborted) return
      console.error('ShapeStore.initData ERROR:', e)
      throw e
    }
  }

  // localStringId/id → shape 매핑 (GetItem O(1) 조회용)
  // 결함 목록/집계가 결함마다 shapes 선형 탐색을 반복(O(n²))하지 않도록 메모이즈
  // 배열 순서상 앞선 shape가 키를 선점해 기존 find의 첫 매칭 의미를 유지한다
  const shapeByKey = computed((): Map<string, Defect> => {
    const map = new Map<string, Defect>()
    for (const shape of shapes.value) {
      const localKey = String(shape.localStringId)
      if (!map.has(localKey)) map.set(localKey, shape)
      const idKey = String(shape.id)
      if (!map.has(idKey)) map.set(idKey, shape)
    }
    return map
  })

  const GetItem = computed(() => {
    return (shapeDatabaseId: string): Defect | undefined => {
      // localStringId와 id 모두로 찾기 (메모이즈된 맵으로 O(1))
      const data = shapeByKey.value.get(`${shapeDatabaseId}`)
      return data ? ({ ...data, shapeDatabaseId: data.localStringId } as Defect) : undefined
    }
  })

  // 판정용 읽기 전용 조회: 전개 복사 없이 store 원본을 돌려준다 (반환값 수정 금지, shapeDatabaseId 별칭 없음)
  // 결함마다 반복 호출되는 집계(사진 유무 판정 등)가 행마다 필드 전체를 추적하며 복사하지 않도록
  const GetItemRaw = computed(() => {
    return (shapeDatabaseId: string): Readonly<Defect> | undefined =>
      shapeByKey.value.get(`${shapeDatabaseId}`)
  })

  // task 전체 localStringId/서버 id → 서버 id 매핑 (SQLite 기준)
  // shapes.value는 현재 층만 담으므로, 이 맵이 없으면 다른 층 결함의 사진 매칭
  // (resolveShapeMatchIds)이 어느 층을 열어 두었는지에 따라 실패한다
  // (로컬 생성 결함 + 서버형 사진 row 조합에서 내용확인 목록·보고서가 "사진 없음"으로 판정)
  const taskServerIdMap = ref<Map<string, string>>(new Map())

  function collectServerIdEntries(
    map: Map<string, string>,
    rows: Array<{ id: unknown; localStringId: unknown }>,
  ): void {
    for (const row of rows) {
      const serverShapeId = String(row.id)
      if (serverShapeId.includes('_need_to_upload')) continue
      map.set(String(row.localStringId), serverShapeId)
      map.set(serverShapeId, serverShapeId)
    }
  }

  async function loadTaskServerIdMap(): Promise<void> {
    if (!SQL.createdTables.has(storeKey) || !currentTaskDatabaseId.value) {
      taskServerIdMap.value = new Map()
      return
    }
    const db = await SQL.getDatabase()
    const result = await db.query(`SELECT id, localStringId FROM ${storeKey} WHERE task_id = ?`, [
      currentTaskDatabaseId.value,
    ])
    const map = new Map<string, string>()
    collectServerIdEntries(
      map,
      (result.values ?? []) as Array<{ id: unknown; localStringId: unknown }>,
    )
    taskServerIdMap.value = map
  }

  // 개별 SQLite 갱신 row를 task 전체 맵에 반영 (층 무관). 변경이 없으면 교체하지 않는다
  function updateTaskServerIdMap(rows: Array<{ id: unknown; localStringId: unknown }>): void {
    // 저장마다 불리므로 과업 전체 Map 복사는 실제 변경이 있을 때 한 번만 한다
    const current = taskServerIdMap.value
    let map: Map<string, string> | null = null
    for (const row of rows) {
      const serverShapeId = String(row.id)
      if (serverShapeId.includes('_need_to_upload')) continue
      const localKey = String(row.localStringId)
      const target = map ?? current
      if (target.get(localKey) === serverShapeId && target.has(serverShapeId)) continue
      if (!map) map = new Map(current)
      map.set(localKey, serverShapeId)
      map.set(serverShapeId, serverShapeId)
    }
    if (map) taskServerIdMap.value = map
  }

  // localStringId/서버 id → 서버 id 문자열 매핑 (사진-도형 매칭용 O(1) 조회)
  // 대량 목록 렌더링에서 GetItem의 선형 탐색을 결함마다 반복하지 않기 위한 메모이즈
  // task 전체 맵 위에 현재 층 메모리(더 최신)를 덮어쓴다
  const shapeServerIdMap = computed((): Map<string, string> => {
    const map = new Map<string, string>(taskServerIdMap.value)
    collectServerIdEntries(map, shapes.value)
    return map
  })

  function getCurrentShapes(floorId: number): Defect[] {
    return shapes.value.filter(shape => shape.floorplan_id === floorId && !shape.deleted_at)
  }

  /**
   * 새로운 shape를 추가합니다
   * @param config - shape 생성 설정
   * @param parentShapeId - 부모 shape ID (attach 시 사용)
   * @returns Promise<void>
   */

  async function AddOnData(newShapeItem: Defect): Promise<Defect> {
    const currentShapes = getCurrentShapes(newShapeItem.floorplan_id)
    const lastSortKey = getLastSortKey(currentShapes)

    const newShape = {
      ...newShapeItem,
      sort_key: lastSortKey,
    }

    const changes: ChangedItem[] = findChangedItems([], [newShape])
    logChanges('SHAPES', changes)
    await requestUpdate([newShape as Defect])

    return newShape as Defect
  }

  function getShapes(floorBranchDatabaseId: number | string): Defect[] {
    return shapes.value
      .filter(shape => !shape.deleted_at)
      .filter(shape => `${shape.floorplan_id}` == `${floorBranchDatabaseId}`)
  }

  async function addTripodShape(
    _newId: string,
    tripodExportData: CanvasEngineExportEntityType,
    targetShapeIds: string[],
  ): Promise<void> {
    const targetShapes = sortByKeys(
      targetShapeIds
        .map(id => shapes.value.find(shape => shape.localStringId.toString() === id))
        .filter((shape): shape is Defect => shape !== undefined),
    )

    if (targetShapes.length === 0) {
      throw new Error('대상 shape를 찾을 수 없습니다.')
    }

    const baseShape = targetShapes[0]
    const baseShapeData = baseShape.shape_data

    // 첫 번째 point의 balloon 설정들을 TRIPOD 데이터에 병합
    const mergedTripodData: CanvasEngineExportEntityType = {
      ...tripodExportData,
      // 첫 번째 point의 balloon 색상과 스케일 설정들 유지 (undefined인 경우만 기본값 사용)
      balloonFillColor: baseShapeData.balloonFillColor ?? tripodExportData.balloonFillColor,
      balloonFontColor: baseShapeData.balloonFontColor ?? tripodExportData.balloonFontColor,
      balloonScale: baseShapeData.balloonScale ?? tripodExportData.balloonScale,
      connectedLineThicknessScale:
        baseShapeData.connectedLineThicknessScale ?? tripodExportData.connectedLineThicknessScale,
      balloonFontScale: baseShapeData.balloonFontScale ?? tripodExportData.balloonFontScale,
      pointShapeScale: baseShapeData.pointShapeScale ?? tripodExportData.pointShapeScale,
      areaBorderThicknessScale:
        baseShapeData.areaBorderThicknessScale ?? tripodExportData.areaBorderThicknessScale,
    }

    // 기존 shape를 기반으로 새로운 TRIPOD shape 생성
    const newShape = {
      ...baseShape,
      id: `${tripodExportData.shapeId}_need_to_upload`,
      shape_type: SHAPES.TRIPOD,
      shape_data: mergedTripodData, // 병합된 TRIPOD 데이터 사용
      selected_tags: [`${getUnixtime()}`],
      localStringId: tripodExportData.shapeId,
      defect_photos: [] as DefectPhoto[],
      deleted_at: null,
      previous_defect_id: -1,
      previous_balloon_number: '',
      admin_index_modified: -1, //임시사용 created_at 처럼 사용
      parent_defect_id: -1,
      parent_shape_id_modified: -1,
      task_id: Number(currentTaskDatabaseId.value),
      modifiedFromLocal: true,
    }

    const changes: ChangedItem[] = findChangedItems([], [newShape])
    logChanges('SHAPES', changes)

    const _defectPhotoStore = useDefectPhotoStore()
    _defectPhotoStore.addDefectPhotoAlreadyUploaded(baseShape.localStringId)

    for (const targetId of targetShapeIds) {
      await deleteShape(targetId)
    }

    await requestUpdate([newShape as Defect])
  }
  async function UpdateValue(updatedShapeItem: Defect): Promise<void> {
    let findShape = shapes.value.find(
      _item => _item.localStringId === updatedShapeItem.localStringId,
    )
    // 현재 층에 없으면 SQLite에서 조회 (다른 층 shape 지원)
    if (!findShape) {
      const db = await SQL.getDatabase()
      const result = await db.query(
        `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
        [updatedShapeItem.localStringId, currentTaskDatabaseId.value],
      )
      if (result.values && result.values.length > 0) {
        findShape = parseRow(result.values[0]) as Defect
      }
    }
    if (!findShape) {
      throw new Error('shape을 찾지 못했습니다.')
    }

    // 변경된 필드 추적
    const fieldsUpdatedAt = Merge.findChangedField(findShape, updatedShapeItem)

    const changedShape = {
      ...updatedShapeItem,
      id: findShape.id,
      sort_key: findShape.sort_key,
      fields_updated_at: fieldsUpdatedAt,
      modifiedFromLocal: true,
    }
    const changes: ChangedItem[] = findChangedItems([findShape], [changedShape])
    logChanges('SHAPES', changes)

    // 즉시 메모리 갱신 (현재 층 shape만)
    const storeIndex = shapes.value.findIndex(
      _item => _item.localStringId === changedShape.localStringId,
    )
    if (storeIndex !== -1) {
      shapes.value.splice(storeIndex, 1, changedShape as Defect)
    }

    await requestUpdate([changedShape])
  }

  /**
   * shape의 geometry 데이터를 업데이트합니다
   * @param config - 업데이트할 shape 설정
   * @returns Promise<void>
   */
  async function UpdateShape(
    config: { shape: CanvasEngineExportEntityType },
    shapeType?: SHAPES,
  ): Promise<void> {
    const findShape = shapes.value.find(item => item.localStringId === config.shape.shapeId)
    if (!findShape) return

    const changedShape = { ...findShape }
    changedShape.shape_data = config.shape
    if (shapeType) {
      changedShape.shape_type = shapeType
      changedShape.shape_data.type = shapeType
    }

    // 변경된 필드 추적
    const fieldsUpdatedAt = Merge.findChangedField(findShape, changedShape)
    changedShape.fields_updated_at = fieldsUpdatedAt
    changedShape.modifiedFromLocal = true

    const changes: ChangedItem[] = findChangedItems([findShape], [changedShape])
    logChanges('SHAPES', changes)
    await requestUpdate([changedShape])
  }
  /**
   * @param shapesParams - 수정된 shape 배열
   * @param originals - 수정 전 원본 배열 (fields_updated_at 계산용)
   */
  async function UpdateShapeAll(shapesParams: Defect[], originals?: Defect[]): Promise<void> {
    if (shapesParams.length === 0) return
    const shapeIds = shapesParams.map(shape => shape.localStringId)

    // Undo/Redo 지원: 기존 큐에 있는 해당 shape 작업들을 먼저 제거
    for (const shapeId of shapeIds) {
      syncQueue.removeJobByLocalStringId(shapeId)
    }

    // 원본 Map 생성 (빠른 조회)
    const originMap = new Map<string, Defect>()
    if (originals) {
      for (const o of originals) originMap.set(o.localStringId, o)
    }

    const changedShapes = shapesParams.map(updatedParams => {
      const original = originMap.get(updatedParams.localStringId) ?? updatedParams
      const fieldsUpdatedAt = Merge.findChangedField(original, updatedParams)
      return {
        ...cloneDeep(updatedParams),
        fields_updated_at: fieldsUpdatedAt,
        modifiedFromLocal: true,
      }
    })

    // 현재 층의 shapes.value도 동기화
    const currentFloorIds = new Set(shapes.value.map(s => s.localStringId))
    for (const changed of changedShapes) {
      if (currentFloorIds.has(changed.localStringId)) {
        const idx = shapes.value.findIndex(s => s.localStringId === changed.localStringId)
        if (idx !== -1) {
          shapes.value[idx] = changed
        }
      }
    }

    const changes: ChangedItem[] = findChangedItems(originals ?? shapesParams, changedShapes)
    logChanges('SHAPES', changes)
    await requestUpdate(changedShapes)
  }

  async function recommandedShapesComputed(
    facilityDatabaseId: string,
    isRange: boolean,
  ): Promise<
    (Defect & {
      groupCount: number
      groupType: 'member-defect' | 'width-length' | 'cause-repair'
    })[]
  > {
    const prefetchedMap = await getShapesByFacilityFromDB(facilityDatabaseId)
    const shapeItems: Defect[] = []
    for (const shapes of prefetchedMap.values()) {
      for (const shape of shapes) {
        if (shape.shape_type !== SHAPES.BRUSH) {
          shapeItems.push({
            id: shape.id,
            classification: shape.classification,
            structural_component: shape.structural_component,
            width: shape.width,
            length: shape.length,
            area: shape.area,
            count: shape.count,
            color: shape.color,
            repair_plan: shape.repair_plan,
            cause_estimate: shape.cause_estimate,
          } as Defect)
        }
      }
    }
    if (shapeItems.length === 0) {
      return []
    }

    const result: (Defect & {
      groupCount: number
      groupType: 'member-defect' | 'width-length' | 'cause-repair'
    })[] = []

    // 그룹1: 부재-결함유형 조합
    const group1Shapes = _.groupBy(
      shapeItems.filter(s => s.structural_component && s.classification),
      shape => `${shape.structural_component}_${shape.classification}`,
    )

    Object.values(group1Shapes).forEach((group): void => {
      const template = { ...group[0] }
      result.push({
        ...template,
        groupCount: group.length,
        groupType: 'member-defect',
      })
    })

    // 그룹2: 폭-길이 조합 (둘 다 있는 경우만)
    const group2Shapes = _.groupBy(
      shapeItems.filter(s => {
        if (!s.width || !s.length) return false

        const isWidthNaN = isNaN(Number(s.width))

        // props.isRange가 true면 NaN인 항목만, false면 NaN이 아닌 항목만
        return isRange ? isWidthNaN : !isWidthNaN
      }),
      shape => `${shape.width}_${shape.length}`,
    )

    Object.values(group2Shapes).forEach((group): void => {
      const template = { ...group[0] }
      // 그룹2는 width, length만 처리 (다른 속성은 첫 번째 항목의 값 사용)

      result.push({
        ...template,
        groupCount: group.length,
        groupType: 'width-length',
      })
    })

    // 그룹3: 원인-보수방안 조합 (둘 다 있는 경우만)
    const filteredGroup3Shapes = shapeItems.filter(s => s.cause_estimate && s.repair_plan)

    const group3Shapes = _.groupBy(
      filteredGroup3Shapes,
      shape => `${shape.cause_estimate}_${shape.repair_plan}`,
    )

    Object.values(group3Shapes).forEach((group): void => {
      const template = { ...group[0] }

      result.push({
        ...template,
        groupCount: group.length,
        groupType: 'cause-repair',
      })
    })

    // 개수 기준으로 내림차순 정렬
    return _.orderBy(result, shape => -shape.groupCount)
  }

  /**
   * shape의 단일 컬럼을 업데이트합니다
   * @param shapeDatabaseId - 업데이트할 shape의 ID
   * @param column - 업데이트할 컬럼명
   * @param value - 업데이트할 값
   * @returns Promise<void>
   */
  async function UpdateShapeOnly(
    shapeDatabaseId: string,
    column: string,
    value: unknown,
  ): Promise<void> {
    const findShape = shapes.value.find(item => item.localStringId === shapeDatabaseId)
    if (!findShape) {
      // 다른 층의 shape: SQLite에서 직접 조회하여 업데이트
      const db = await SQL.getDatabase()
      const result = await db.query(
        `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
        [shapeDatabaseId, currentTaskDatabaseId.value],
      )
      if (!result.values || result.values.length === 0) {
        return
      }
      const dbShape = parseRow(result.values[0]) as Defect
      const changedShape = { ...dbShape }
      ;(changedShape as Record<string, unknown>)[column] = value
      const existingFieldsUpdatedAt = dbShape.fields_updated_at || {}
      const fieldsUpdatedAt = Merge.setFieldUpdate(existingFieldsUpdatedAt, column)
      changedShape.fields_updated_at = fieldsUpdatedAt
      changedShape.modifiedFromLocal = true
      const changes: ChangedItem[] = findChangedItems([dbShape], [changedShape])
      logChanges('SHAPES', changes)
      await requestUpdate([changedShape])
      return
    }

    const changedShape = { ...findShape }
    /* eslint-disable @typescript-eslint/no-explicit-any */
    ;(changedShape as any)[column] = value
    /* eslint-enable @typescript-eslint/no-explicit-any */

    // 변경된 필드 추적 - 단일 필드만 업데이트
    const existingFieldsUpdatedAt = findShape.fields_updated_at || {}
    const fieldsUpdatedAt = Merge.setFieldUpdate(existingFieldsUpdatedAt, column)
    changedShape.fields_updated_at = fieldsUpdatedAt
    changedShape.modifiedFromLocal = true

    shapes.value = shapes.value.map(shapeItem => {
      if (shapeItem.localStringId === shapeDatabaseId) {
        return changedShape
      }
      return shapeItem
    })

    const changes: ChangedItem[] = findChangedItems([findShape], [changedShape])
    logChanges('SHAPES', changes)
    await requestUpdate([changedShape])
  }

  /**
   * shape의 레이어 ID와 색상을 업데이트합니다
   * @param shapeId - 업데이트할 shape의 ID
   * @param color - 색상
   * @returns Promise<void>
   */
  async function updateColor(
    shapeId: string,
    color: string,
    balloonFontColor: string,
    balloonFillColor: string,
  ): Promise<void> {
    const findShape = shapes.value.find(item => item.localStringId == shapeId)
    if (!findShape) return
    const previousShape = cloneDeep(findShape)
    const colorName = getCurrentColorName(color)

    const changedShape = { ...findShape }
    //색상 먼저 업데이트
    changedShape.color = color
    changedShape.shape_data.color = color
    changedShape.shape_data.balloonFontColor = balloonFontColor
    changedShape.shape_data.balloonFillColor = balloonFillColor
    changedShape.shape_data.isChanged = isPreviousShape(
      changedShape.previous_defect_id?.toString() ?? '',
    )
      ? false
      : true
    //이제 레이어 업데이트
    //전차인데 사진이 있는지 확인
    const defectPhotoStore = useDefectPhotoStore()
    const isPreviousHasDefectPhoto = defectPhotoStore.getIsPreviousHasDefectCurrentPhoto(shapeId)
    if (isPreviousShape(changedShape.previous_defect_id)) {
      if (
        isPreviousHasDefectPhoto &&
        colorName != 'purple' &&
        getCurrentColorName(findShape.color) == 'purple'
      ) {
        const lastSortKey = getLastSortKey(
          shapes.value.filter(
            defect =>
              `${defect.floorplan_id}` == `${findShape.floorplan_id}` &&
              !defect.deleted_at &&
              defect.shape_type !== SHAPES.BRUSH &&
              (!isPreviousShape(defect.previous_defect_id?.toString() ?? '') ||
                (isPreviousShape(defect.previous_defect_id?.toString() ?? '') &&
                  defectPhotoStore.getIsPreviousHasDefectPhoto(defect.localStringId.toString()))),
          ),
        )
        changedShape.sort_key = lastSortKey
      }
    }

    // 변경된 필드 추적
    const fieldsUpdatedAt = Merge.findChangedField(previousShape, changedShape)
    changedShape.fields_updated_at = fieldsUpdatedAt
    changedShape.modifiedFromLocal = true

    shapes.value = shapes.value.map(shapeItem => {
      if (shapeItem.localStringId == shapeId) {
        return changedShape
      }
      return shapeItem
    })

    const changes: ChangedItem[] = findChangedItems([previousShape], [changedShape])
    logChanges('SHAPES', changes)
    await requestUpdate([changedShape])
  }

  /**
   * 업로드되지 않은 shape를 soft delete 처리 (관련 사진, 이벤트 로그 포함)
   * @param {string} shapeId - shape의 localStringId
   * @param {Defect} changedShape - 변경된 shape 데이터
   * @param {Defect} findShape - 기존 shape 데이터
   * @returns {Promise<void>}
   */
  async function deleteNotUploadShape(
    shapeId: string,
    changedShape: Defect,
    findShape: Defect,
  ): Promise<void> {
    // 메모리에서 soft delete 처리
    shapes.value = shapes.value.map(shape => {
      if (shape.localStringId == shapeId) {
        return { ...shape, deleted_at: changedShape.deleted_at, modifiedFromLocal: false }
      }
      return shape
    })

    // SQLite에서 업데이트
    const db = await SQL.getDatabase()
    await db.query(
      'UPDATE defects SET deleted_at = ?, modifiedFromLocal = ? WHERE localStringId = ?',
      [changedShape.deleted_at, 0, shapeId],
    )

    // 업데이트 큐에서 제거
    syncQueue.removeJobByLocalStringId(findShape.localStringId)

    // 관련된 이벤트 로그도 soft delete
    const eventLogStore = useDeviceEventLogStore()
    await eventLogStore.deleteLogs(findShape.localStringId, changedShape.deleted_at!)

    // 관련된 사진도 soft delete
    const defectPhotoStore = useDefectPhotoStore()
    await defectPhotoStore.deleteNotUploadDefectPhotos(
      findShape.localStringId,
      changedShape.deleted_at!,
    )
  }

  /**
   * soft delete된 shape를 복원 (관련 사진, 이벤트 로그 포함)
   * @param {string} shapeId - shape의 localStringId
   * @returns {Promise<void>}
   */
  async function restoreNotUploadShape(shapeId: string): Promise<void> {
    // 메모리에서 복원 처리
    shapes.value = shapes.value.map(shape => {
      if (shape.localStringId == shapeId && shape.deleted_at) {
        return { ...shape, deleted_at: null, modifiedFromLocal: true }
      }
      return shape
    })

    // SQLite에서 업데이트
    const db = await SQL.getDatabase()
    await db.query(
      'UPDATE defects SET deleted_at = NULL, modifiedFromLocal = ? WHERE localStringId = ?',
      [1, shapeId],
    )

    // 관련된 사진도 복원
    const defectPhotoStore = useDefectPhotoStore()
    await defectPhotoStore.restoreNotUploadDefectPhotos(shapeId)
  }

  /**
   * @param floorDatabaseId - 조회할 층 ID
   * @param selectedFloorName - 선택된 층 이름 (BRUSH 인덱싱용)
   * @param facilityDatabaseId - 시설물 ID (전체 층 인덱싱 계산용)
   * @param prefetchedMap - 미리 조회한 facility 전체 shape Map (1회 쿼리 최적화)
   */
  async function getFacilityFloorShapes(
    floorDatabaseId: string,
    selectedFloorName: string,
    facilityDatabaseId?: string,
    prefetchedMap?: Map<string, Defect[]>,
  ): Promise<Defect[]> {
    // prefetchedMap이 있으면 해당 층 데이터를 꺼내고, 없으면 개별 쿼리
    const floorShapesFromDB = prefetchedMap
      ? (prefetchedMap.get(floorDatabaseId) ?? [])
      : await getFloorShapesFromDB(floorDatabaseId)
    const sortedShapes = sortShapesWithChildren(floorShapesFromDB)
    const photoStore = useDefectPhotoStore()
    const canvasStore = useCanvasStore()
    let indexing = 0

    // 1. 전체 층 인덱싱 계산 (isAllFloorNumbering 활성화 시)
    if (canvasStore.isAllFloorNumbering && facilityDatabaseId) {
      const floorStore = useFloorplanStore()
      const floorplans = floorStore.getFloorplans(facilityDatabaseId)

      if (floorplans) {
        const sortedFloors = sortByKeys(floorplans)
        const currentFloorIndex = sortedFloors.findIndex(
          floor => floor.id.toString() === floorDatabaseId,
        )

        for (let i = 0; i < currentFloorIndex; i++) {
          const prevFloorId = sortedFloors[i].id.toString()
          const prevFloorShapes = prefetchedMap
            ? (prefetchedMap.get(prevFloorId) ?? [])
            : await getFloorShapesFromDB(prevFloorId)
          const floorShapes = sortShapesWithChildren(prevFloorShapes)
          indexing += floorShapes.filter(shape => {
            if (shape.shape_type === SHAPES.BRUSH || shape.parent_defect_id) return false

            const isPrev = isPreviousShape(shape.previous_defect_id)
            if (isPrev) {
              const hasCurrentPhoto = photoStore.getIsPreviousHasDefectPhoto(
                shape.localStringId.toString(),
              )
              if (isUnplacedPrevDefect(shape, hasCurrentPhoto)) return false
              return shape.progressing === 1 || hasCurrentPhoto
            }
            return true
          }).length
        }
      }
    }

    // 2. [최적화] 부모 요소를 O(1)로 찾기 위한 Map 생성
    const shapeMap = new Map<string | number, Defect>()
    sortedShapes.forEach(s => {
      shapeMap.set(s.id, s)
      if (s.localStringId) shapeMap.set(s.localStringId, s)
    })

    // 3. 데이터 가공 및 인덱싱 부여
    const processedShapes = sortedShapes.map(shape => {
      const shapeIdStr = shape.localStringId.toString()
      const allTypePhotos = photoStore.allTypePhotos(shapeIdStr)
      const newPhotos = allTypePhotos.newTargetPhotos ?? []
      const oldPhotos = allTypePhotos.prevTargetPhotos ?? []

      // 기본 정보 설정
      shape.floorplan_id = parseInt(floorDatabaseId)
      shape.hasPhoto = isPreviousShape(shape.previous_defect_id?.toString() ?? '')
        ? photoStore.getIsPreviousHasDefectPhoto(shapeIdStr)
        : newPhotos.length > 0
      shape.mainPhotoUrl = newPhotos[0]?.url ?? ''
      shape.prevPhotoUrl = oldPhotos[0]?.url ?? ''

      // 인덱싱 로직
      if (shape.shape_type === SHAPES.BRUSH) {
        shape.indexing = selectedFloorName
      } else if (shape.parent_defect_id) {
        const parent = shapeMap.get(shape.parent_defect_id as string | number)
        shape.indexing = parent?.indexing ?? 0
      } else if (isPreviousShape(shape.previous_defect_id?.toString() ?? '')) {
        const isPreviousHasDefectPhoto = photoStore.getIsPreviousHasDefectPhoto(shapeIdStr)
        if (isUnplacedPrevDefect(shape, isPreviousHasDefectPhoto)) {
          // 도형 없는 전차(보고서 가져오기): 진행상황과 무관하게 보고서 번호를 보여 주고 순번을 소비하지 않는다
          shape.indexing = shape.previous_balloon_number ?? ''
        } else if (shape.progressing == 1 || isPreviousHasDefectPhoto) {
          indexing++
          shape.indexing = indexing
        } else {
          shape.indexing = shape.previous_balloon_number ?? 0
        }
      } else {
        indexing++
        shape.indexing = indexing
      }

      return shape
    })

    // 4. BRUSH 타입 제외 후 결과 반환
    const finalShapes = processedShapes.filter(shape => shape.shape_type !== SHAPES.BRUSH)
    return finalShapes
  }

  async function getFacilityNumberingShapes(
    facilityDatabaseId: string,
    selectedFloorName: string,
  ): Promise<Defect[]> {
    const floorStore = useFloorplanStore()
    const canvasStore = useCanvasStore()
    const defectPhotoStore = useDefectPhotoStore()

    // 해당 시설의 모든 층을 location 순서대로 가져오기
    const currentFloors = floorStore.getFloorplans(facilityDatabaseId)
    if (!currentFloors) return []

    const sortedFloors = sortByKeys(currentFloors)

    // facility 전체 shape를 1회 쿼리로 조회 — 가공은 시간 예산마다 양보(층 진입 프레임을 막지 않게)
    const yieldBudgetMs = 25
    const prefetchedMap = await getShapesByFacilityFromDB(facilityDatabaseId, yieldBudgetMs)
    const yieldIfDue = createYielder(yieldBudgetMs)

    // 전체 결과를 담을 배열
    const allShapes: Defect[] = []
    let globalIndexing = 0

    const isAllFloorNumbering = canvasStore.isAllFloorNumbering

    // 모든 층에 대해 shape 수집
    for (const floor of sortedFloors) {
      await yieldIfDue()
      const floorIdStr = floor.id.toString()

      // prefetchedMap을 전달하여 추가 쿼리 없이 처리
      const allFloorShapes = await getFacilityFloorShapes(
        floorIdStr,
        selectedFloorName,
        facilityDatabaseId,
        prefetchedMap,
      )
      const sortedFloorShapes = sortShapesWithChildren(allFloorShapes)

      // 전체 floor 인덱싱이 활성화된 경우 연속적인 인덱싱 적용
      if (isAllFloorNumbering) {
        for (const shape of sortedFloorShapes) {
          // BRUSH 타입은 스킵
          if (shape.shape_type === SHAPES.BRUSH) continue

          const previousDefectId = shape.previous_defect_id
          const isPrevious = isPreviousShape(previousDefectId)

          // 인덱싱 조건 체크
          let shouldIndex = false

          if (isPrevious) {
            const hasCurrentPhoto = defectPhotoStore.getIsPreviousHasDefectPhoto(
              shape.localStringId.toString(),
            )
            // 도형 없는 전차(보고서 가져오기)는 순번을 소비하지 않음
            if (isUnplacedPrevDefect(shape, hasCurrentPhoto)) {
              shouldIndex = false
            }
            // 전차 항목이면서 완료 항목인 경우
            else if (shape.progressing === 1) {
              shouldIndex = true
            }
            // 전차 항목이면서 진행중인 경우, defect photo가 있는 경우만
            else if (hasCurrentPhoto) {
              shouldIndex = true
            }
          } else {
            // 그 외의 경우 (일반적인 현회차 항목)
            shouldIndex = true
          }

          if (shouldIndex) {
            globalIndexing++
            shape.indexing = globalIndexing
          }
        }
      }

      // 배열 펼치기 대신 직접 추가 (더 효율적)
      for (const shape of sortedFloorShapes) {
        allShapes.push(shape)
      }
    }

    return allShapes
  }

  /**
   * 번호 시작값 계산용 — 주어진 층들의 shape를 번호 판정·정렬에 필요한 열만 조회해 getFacilityNumberingShapes와
   * 같은 집합(층별 부모·자식 정렬 → BRUSH 제외 → 다시 정렬)으로 돌려준다. 층 진입에서 앞 층 개수만 필요한데
   * 시설 전체 행을 전 열(브러시 점 JSON 포함)로 가공하면 A21s 도형 많은 층에서 브리지 수신·GC가 한 프레임을 막았다
   */
  async function getNumberingCountShapes(floorIds: string[]): Promise<Defect[]> {
    if (floorIds.length === 0 || !SQL.createdTables.has(storeKey)) return []
    const placeholders = floorIds.map(() => '?').join(',')
    const db = await SQL.getDatabase()
    const queryResult = await db.query(
      `SELECT id, localStringId, parent_defect_id, shape_type, previous_defect_id, progressing, floorplan_id, sort_key, created_at FROM ${storeKey} WHERE floorplan_id IN (${placeholders}) AND task_id = ? AND (deleted_at IS NULL OR deleted_at = '')`,
      [...floorIds, currentTaskDatabaseId.value],
    )
    const byFloor = new Map<string, Defect[]>()
    for (const row of queryResult.values ?? []) {
      const shape = parseRow(row) as Defect
      const fid = shape.floorplan_id.toString()
      let list = byFloor.get(fid)
      if (!list) {
        list = []
        byFloor.set(fid, list)
      }
      list.push(shape)
    }
    const result: Defect[] = []
    for (const floorId of floorIds) {
      const sorted = sortShapesWithChildren(byFloor.get(floorId) ?? [])
      for (const shape of sorted) shape.floorplan_id = parseInt(floorId)
      const floorShapes = sortShapesWithChildren(
        sorted.filter(shape => shape.shape_type !== SHAPES.BRUSH),
      )
      for (const shape of floorShapes) result.push(shape)
    }
    return result
  }

  function isCheckPreviousShape(shapeId: string): boolean {
    const findShape = shapes.value.find(item => item.localStringId == shapeId)
    if (!findShape) return false
    if (findShape.shape_type == SHAPES.BRUSH) return false
    const defectPhotoStore = useDefectPhotoStore()
    const isPreviousHasDefectPhoto = defectPhotoStore.getIsPreviousHasDefectPhoto(shapeId)
    return isPreviousShape(findShape.previous_defect_id) && !isPreviousHasDefectPhoto
  }

  // 전차↔현차 전환 시 상태 캡처/복원 헬퍼
  function captureShapeColorState(shapeId: string): {
    is_current: number
    color: string
    balloonFontColor: string
    balloonFillColor: string
  } | null {
    const shape = shapes.value.find(item => item.localStringId == shapeId)
    if (!shape) return null
    return {
      is_current: shape.is_current ?? 0,
      color: shape.color,
      balloonFontColor: shape.shape_data?.balloonFontColor ?? '',
      balloonFillColor: shape.shape_data?.balloonFillColor ?? '',
    }
  }

  async function restoreShapeColorState(
    shapeId: string,
    state: {
      is_current: number
      color: string
      balloonFontColor: string
      balloonFillColor: string
    },
  ): Promise<void> {
    const canvasStore = useCanvasStore()
    const findShape = shapes.value.find(item => item.localStringId == shapeId)
    if (!findShape) return
    const changedShape = {
      ...cloneDeep(findShape),
      is_current: state.is_current,
      color: state.color,
      modifiedFromLocal: true,
    }
    changedShape.shape_data = {
      ...changedShape.shape_data,
      balloonFontColor: state.balloonFontColor,
      balloonFillColor: state.balloonFillColor,
    }
    changedShape.fields_updated_at = Merge.findChangedField(findShape, changedShape)
    shapes.value = shapes.value.map(s => (s.localStringId == shapeId ? changedShape : s))
    await requestUpdate([changedShape])
    const isPurple = state.color === PREV_DEFECT_STYLE.color
    onColorChanged(shapeId, state.color, state.balloonFontColor, state.balloonFillColor, true)
    const entity = canvasStore.getEntityById(shapeId)
    if (entity) {
      if (isPurple) entity.disableIndex()
      else entity.enableIndex()
    }
    canvasStore.updateIndex()
  }

  // 전차결함 ↔ 현재차가된전차결함 수동 전환 (사진 없이 현재차로 취급)
  // is_current + 색상 4필드를 단일 트랜잭션으로 변경 → fields_updated_at가 한 번에 일관 기록되고
  // requestUpdate도 1회만 호출되어 동기화 손실 차단.
  async function setIsBecomeCurrent(shapeId: string, value: number): Promise<void> {
    const undoRedo = useUndoRedo()
    const findShape = shapes.value.find(item => item.localStringId == shapeId)
    if (!findShape || !isPreviousShape(findShape.previous_defect_id)) return

    // 전환 전 상태 캡처 (undo용)
    const beforeState = captureShapeColorState(shapeId)
    if (!beforeState) return

    const previousShape = cloneDeep(findShape)
    // shape_data 내부 mutation을 위해 깊은 복사
    const changedShape = cloneDeep(findShape)
    changedShape.is_current = value

    // 색상 계산 (savedCurrentStyles 보존/복원 포함)
    const colors =
      value === 1 ? calcBecomeCurrentColors(changedShape) : calcBecomePreviousColors(changedShape)

    // 색상 4필드를 한 번에 적용
    changedShape.color = colors.borderColor
    if (!changedShape.shape_data) {
      changedShape.shape_data = {} as Defect['shape_data']
    }
    changedShape.shape_data.color = colors.borderColor
    changedShape.shape_data.balloonFontColor = colors.fontColor
    changedShape.shape_data.balloonFillColor = colors.fillColor
    // 전차 결함은 isChanged=false (updateColor 패턴과 일치)
    changedShape.shape_data.isChanged = false

    // fields_updated_at 1회 계산: is_current + color + shape_data + shape_data.색상필드 모두 포함됨
    changedShape.fields_updated_at = Merge.findChangedField(previousShape, changedShape)
    changedShape.modifiedFromLocal = true

    // store 업데이트 + SQLite/API 큐 1회 호출
    shapes.value = shapes.value.map(s => (s.localStringId == shapeId ? changedShape : s))
    const changes: ChangedItem[] = findChangedItems([previousShape], [changedShape])
    logChanges('SHAPES', changes)
    await requestUpdate([changedShape])

    // 캔버스 entity 즉시 갱신 (Step E: stale 캔버스 차단)
    const _canvasStore = useCanvasStore()
    const entity = _canvasStore.getEntityById(shapeId)
    if (entity) {
      entity.changeColor(colors.borderColor)
      entity.setBalloonFontColor(colors.fontColor)
      entity.setBalloonFillColor(colors.fillColor)
      if (value === 1) {
        entity.enableIndex()
      } else {
        if (changedShape.previous_balloon_number) {
          entity.setBalloonAdminText(changedShape.previous_balloon_number)
        }
        entity.disableIndex()
      }
      entity.invalidateCache()
    }
    _canvasStore.updateIndex()

    // 전환 후 상태 캡처 → 하나의 undo 엔트리로 등록
    const afterState = captureShapeColorState(shapeId)
    if (!afterState) return
    undoRedo.execute(
      value ? '현재차 전환이' : '전차 전환이',
      async () => restoreShapeColorState(shapeId, beforeState),
      async () => restoreShapeColorState(shapeId, afterState),
    )
  }

  async function getFacilityShapes(
    facilityDatabaseId: string,
    selectedFloorName: string,
  ): Promise<Defect[]> {
    const floorStore = useFloorplanStore()
    const floorplans = floorStore.getFloorplans(facilityDatabaseId)
    if (!floorplans) return []

    const sortedFloors = sortByKeys(floorplans)

    // facility 전체 shape를 1회 쿼리로 조회
    const prefetchedMap = await getShapesByFacilityFromDB(facilityDatabaseId)

    const currentShapes = []
    for (const floor of sortedFloors) {
      const floorShapes = await getFacilityFloorShapes(
        floor.id.toString(),
        selectedFloorName,
        facilityDatabaseId,
        prefetchedMap,
      )
      const sortedFloorShapes = sortShapesWithChildren(floorShapes)
      currentShapes.push(...sortedFloorShapes)
    }
    return currentShapes
  }

  /**
   * 기존 shape를 복사하여 새로운 shape를 생성합니다
   * @param shapeId - 복사할 원본 shape의 ID
   * @param copiedShape - 복사된 shape 데이터
   * @returns 복사된 shape
   */
  async function copyShape(
    shapeId: string,
    copiedShape: CanvasEngineExportEntityType,
  ): Promise<Defect | null> {
    const findShape = cloneDeep(shapes.value.find(item => item.localStringId === shapeId))
    if (!findShape) return null
    const defectPhotoStore = useDefectPhotoStore()

    const newId = `${copiedShape.shapeId}_need_to_upload`
    const newShape = { ...findShape }
    newShape.id = newId
    newShape.localStringId = copiedShape.shapeId
    newShape.shape_data = { ...findShape.shape_data, ...copiedShape, shapeId: copiedShape.shapeId }

    const lastSortKey = getLastSortKey(
      shapes.value.filter(
        defect =>
          `${defect.floorplan_id}` == `${findShape.floorplan_id}` &&
          !defect.deleted_at &&
          defect.shape_type !== SHAPES.BRUSH &&
          (!isPreviousShape(defect.previous_defect_id?.toString() ?? '') ||
            (isPreviousShape(defect.previous_defect_id?.toString() ?? '') &&
              defectPhotoStore.getIsPreviousHasDefectPhoto(defect.localStringId.toString()))),
      ),
    )
    newShape.sort_key = lastSortKey
    newShape.modifiedFromLocal = true

    const changes: ChangedItem[] = findChangedItems([], [newShape])
    logChanges('SHAPES', changes)
    await requestUpdate([newShape])

    return newShape
  }

  function allClear(): void {
    // 리스너 해제
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('defects', currentListener)
      currentListener = null
    }
    shapes.value = []
    taskServerIdMap.value = new Map()
    activeFloorplanId.value = ''
  }

  function updateShapes(): void {
    const floorId = useAppEditorFacilityStore().selectedFloorDatabaseId
    const canvasStore = useCanvasStore()
    if (!floorId) return
    const sortedShapes = getSortedShapes(floorId)
    const shapeIds = sortedShapes.map(shape => shape.localStringId.toString())
    canvasStore.sortEntities(shapeIds)
    canvasStore.refreshCache()
  }

  async function deleteShapeToHistory(
    shapeId: number | string,
    currentIndex: number,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const shape = shapes.value.find(shape => `${shape.localStringId}` == `${shapeId}`)
    if (!shape) return
    await deleteShape(shapeId.toString())
    // undo에서 복원된 shape ID를 추적하기 위한 변수
    const currentShapeId = shape.localStringId
    // 히스토리에만 추가 (이미 실행된 상태)
    undoRedo.execute(
      '결함 삭제가',
      // 복원 (undo)
      async () => {
        await restoreShape(currentShapeId)
        const newShapeResult = shapes.value.find(item => item.localStringId == currentShapeId)
        if (!newShapeResult) return
        canvasStore.canvasAddShape(newShapeResult, currentIndex - 1)
        updateShapes()
      },
      // 재삭제 (redo)
      async () => {
        await deleteShape(currentShapeId)
        canvasStore.canvasDeleteShape(currentShapeId)
      },
    )
  }

  /**
   * 여러 shape를 한 번에 삭제합니다 (undo/redo 지원)
   * @param shapeIds - 삭제할 shape ID 배열
   * @param currentIndices - 각 shape의 현재 인덱스 맵
   * @returns Promise<void>
   */
  async function deleteMultipleShapesToHistory(
    shapeIds: string[],
    currentIndices: Map<string, number>,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    // 삭제할 shape들 찾기
    const targetShapes = shapes.value.filter(
      shape => shapeIds.includes(shape.localStringId) || shapeIds.includes(shape.id.toString()),
    )
    if (targetShapes.length === 0) return

    // 삭제 전 shape 데이터 백업
    const backupShapes = cloneDeep(targetShapes)

    // 모든 shape 데이터 삭제
    await deleteMultipleShapes(targetShapes.map(shape => shape.localStringId))

    // undo/redo 등록
    undoRedo.execute(
      `${targetShapes.length}개 결함 삭제가`,
      // 복원 (undo)
      async () => {
        const currentIndexes = backupShapes.map(shape => {
          return currentIndices.get(shape.localStringId) || 0
        })
        await restoreShapes(backupShapes)
        UTIL.createMultipleEntities(backupShapes, currentIndexes)
        updateShapes()
      },
      // 재삭제 (redo)
      async () => {
        await deleteMultipleShapes(backupShapes.map(shape => shape.localStringId))
        UTIL.deleteMultipleEntities(backupShapes.map(shape => shape.localStringId))
      },
    )
  }

  async function copyShapeToHistory(
    shapeId: string,
    copiedShape: CanvasEngineExportEntityType,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const originalShape = cloneDeep(shapes.value.find(x => x.localStringId == shapeId))
    if (!originalShape) return
    // 먼저 shape 생성
    copyShape(shapeId, copiedShape)

    canvasStore.selectShapeById(copiedShape.shapeId)
    // undo/redo 등록
    undoRedo.execute(
      '결함 복사가',
      // 삭제 (undo)
      async () => {
        await deleteShape(copiedShape.shapeId.toString())
        canvasStore.canvasDeleteShape(copiedShape.shapeId.toString())
      },
      // 재생성 (redo)
      async () => {
        copiedShape.shapeId = canvasStore.generateShapeId() + '_need_to_upload'
        copyShape(shapeId, copiedShape)
        const findShape = shapes.value.find(item => item.localStringId == copiedShape.shapeId)
        if (!findShape) return
        canvasStore.canvasAddShape(findShape)
        updateShapes()
        canvasStore.selectShapeById(copiedShape.shapeId)
      },
    )
  }

  async function onColorChanged(
    shapeId: string,
    color: string,
    fontColor: string,
    fillColor: string,
    isPreviousShape: boolean,
  ): Promise<void> {
    const canvasStore = useCanvasStore()
    const isPurpleColor = color == PREV_DEFECT_STYLE.color
    const currentType = isPreviousShape ? (isPurpleColor ? 'previous' : 'new') : 'new'
    const entity = canvasStore.getEntityById(shapeId.toString())
    if (!entity) return

    canvasStore.changeSelectedEntityColorForPreview(shapeId, color)
    entity.setBalloonFontColor(fontColor)
    entity.setBalloonFillColor(fillColor)
    // 캐시된 이미지가 stale 상태가 되므로 즉시 갱신하여 캔버스에 반영
    entity.invalidateCache()
    if (currentType === 'previous') {
      entity.disableIndex()
    } else {
      entity.enableIndex()
    }
    updateShapes()
    canvasStore.updateIndex()
    UTIL.refreshCache()
  }

  async function updateColorToHistory(
    shapeId: string,
    color: string,
    fontColor: string,
    fillColor: string,
    isPreviousShape: boolean,
    isPhotoChanged?: boolean,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const findShape = shapes.value.find(shape => `${shape.localStringId}` == `${shapeId}`)
    if (!findShape) return
    const originalColor = cloneDeep(findShape.color)
    const originalBalloonFontColor = cloneDeep(findShape.shape_data.balloonFontColor)
    const originalBalloonFillColor = cloneDeep(findShape.shape_data.balloonFillColor)
    await updateColor(shapeId, color, fontColor, fillColor)
    onColorChanged(shapeId, color, fontColor, fillColor, isPreviousShape)
    if (isPhotoChanged) return

    undoRedo.execute(
      '결함 색상 변경이',
      async () => {
        updateColor(
          shapeId,
          originalColor,
          originalBalloonFontColor || getCurrentColor('red'),
          originalBalloonFillColor || getCurrentColor('none'),
        )

        onColorChanged(
          shapeId,
          originalColor,
          originalBalloonFontColor || getCurrentColor('red'),
          originalBalloonFillColor || getCurrentColor('none'),
          isPreviousShape,
        )
      },
      async () => {
        updateColor(shapeId, color, fontColor, fillColor)

        onColorChanged(shapeId, color, fontColor, fillColor, isPreviousShape)
      },
    )
  }

  async function updateShapeToHistory(shape: Defect, isReloadShape: boolean = true): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const shapeParams = cloneDeep(shape)
    let beforeShapeParams = cloneDeep(
      shapes.value.find(x => x.localStringId == shape.localStringId),
    )
    // 현재 층에 없으면 SQLite에서 조회
    if (!beforeShapeParams) {
      const db = await SQL.getDatabase()
      const result = await db.query(
        `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
        [shape.localStringId, currentTaskDatabaseId.value],
      )
      if (result.values && result.values.length > 0) {
        beforeShapeParams = cloneDeep(parseRow(result.values[0]) as Defect)
      }
    }
    if (!beforeShapeParams) return
    await UpdateValue(shapeParams)

    undoRedo.execute(
      '결함 업데이트가',
      // 복원 (undo)
      async () => {
        const needUpdateShape = {
          ...beforeShapeParams,
          // 다른 키값에 _modified를 붙인 키값의 데이터를 getUnixtime()으로 변경
        }
        await UpdateValue(needUpdateShape)
        if (isReloadShape) {
          canvasStore.reloadShape({ ...needUpdateShape })
          updateShapes()
        }
      },
      // 재생성 (redo)
      async () => {
        const needUpdateShape = {
          ...shapeParams,
          // 다른 키값에 _modified를 붙인 키값의 데이터를 getUnixtime()으로 변경
        }
        await UpdateValue(needUpdateShape)
        if (isReloadShape) {
          canvasStore.reloadShape({ ...needUpdateShape })
          updateShapes()
        }
      },
    )
  }

  // sort_key만 변경하는 함수 (UpdateValue의 sort_key 보존 우회)
  async function updateSortKey(localStringId: string, newSortKey: string): Promise<void> {
    const findShape = shapes.value.find(item => item.localStringId === localStringId)
    if (!findShape) return
    const changedShape = {
      ...cloneDeep(findShape),
      sort_key: newSortKey,
      modifiedFromLocal: true,
    }
    const fieldsUpdatedAt = Merge.findChangedField(findShape, changedShape)
    changedShape.fields_updated_at = fieldsUpdatedAt
    await requestUpdate([changedShape])
  }

  // 캐시 클리어 + 엔티티 정렬 + 사이드바 리렌더
  function refreshCanvasOrderFromStore(): void {
    const canvasStore = useCanvasStore()
    const engine = canvasStore.getCanvasEngine()
    if (engine) engine.clearAllEntityCache()
    updateShapes()
    canvasStore.bumpIndexVersion()
  }

  // sort_key 변경 + undo/redo 지원 래퍼
  async function updateSortKeyToHistory(localStringId: string, newSortKey: string): Promise<void> {
    const undoRedo = useUndoRedo()
    const findShape = shapes.value.find(item => item.localStringId === localStringId)
    if (!findShape) return
    const beforeSortKey = findShape.sort_key

    await updateSortKey(localStringId, newSortKey)

    undoRedo.execute(
      '번호 변경이',
      async () => {
        await updateSortKey(localStringId, beforeSortKey)
        refreshCanvasOrderFromStore()
      },
      async () => {
        await updateSortKey(localStringId, newSortKey)
        refreshCanvasOrderFromStore()
      },
    )
  }

  function getCurrentDefect(defectId: string): Defect | undefined {
    return shapes.value.find(x => x.localStringId.toString() == defectId.toString())
  }

  async function getUpdateTargetShapes(
    floorId: number,
    isAllFloor: boolean = false,
  ): Promise<Defect[]> {
    const allTaskStore = useAllTaskStore()
    const facilityFloorId = allTaskStore.getFacilityFloorId(floorId)
    if (!facilityFloorId) return []
    const facilityName = allTaskStore.getFacilityName(facilityFloorId.toString())
    const targetShapes = isAllFloor
      ? await getFacilityShapes(facilityFloorId.toString(), facilityName)
      : currentFloorBranchShapes(floorId.toString())

    if (!targetShapes || targetShapes.length === 0) return []
    return targetShapes.filter(shape => shape.shape_type !== SHAPES.BRUSH)
  }
  async function updateFloorOrFacilityShapes(
    floorBranchDatabaseId: string,
    shapeCanvasData: Partial<CanvasEngineExportEntityType>,
    isAllFloor: boolean = false,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const defectPhotoStore = useDefectPhotoStore()
    const canvasStore = useCanvasStore()

    const currentShapes = await getUpdateTargetShapes(Number(floorBranchDatabaseId), isAllFloor)
    if (currentShapes.length === 0) return
    const targetShapes = currentShapes.filter(shape => shape.shape_type !== SHAPES.BRUSH)

    if (!targetShapes.length) return
    // 이전 shape 데이터 백업
    const originalShapes = cloneDeep(targetShapes)

    // 각 shape의 shape_data를 기존 + 새로운 데이터로 업데이트
    const updatedShapes = targetShapes.map(shape => {
      if (
        !isPreviousShape(shape.previous_defect_id ?? '') ||
        (isPreviousShape(shape.previous_defect_id ?? '') &&
          defectPhotoStore.getIsPreviousHasDefectPhoto(shape.localStringId))
      ) {
        return {
          ...shape,
          shape_data: {
            ...shape.shape_data,
            ...shapeCanvasData,
          },
          color: shapeCanvasData.color || shape.color,
        }
      } else {
        //전차는 색상변경 하지않음
        return {
          ...shape,
          shape_data: {
            ...shape.shape_data,
            ...pickShapeScaleFields(shapeCanvasData),
          },
        }
      }
    })
    const clonedUpdatedShapes = cloneDeep(updatedShapes)

    await UpdateShapeAll(updatedShapes, originalShapes)

    undoRedo.execute(
      '결함 일괄 변경이',
      // 복원 (undo)
      async () => {
        await UpdateShapeAll(originalShapes, clonedUpdatedShapes)
        originalShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
      },
      // 재생성 (redo)
      async () => {
        await UpdateShapeAll(clonedUpdatedShapes, originalShapes)
        clonedUpdatedShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
      },
    )
  }

  async function updateShapeOnlyToHistory(
    shapeDatabaseId: string,
    column: string,
    value: unknown,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    let shapeParams = cloneDeep(shapes.value.find(x => x.localStringId == shapeDatabaseId))
    // 현재 층에 없으면 SQLite에서 조회
    if (!shapeParams) {
      const db = await SQL.getDatabase()
      const result = await db.query(
        `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
        [shapeDatabaseId, currentTaskDatabaseId.value],
      )
      if (result.values && result.values.length > 0) {
        shapeParams = cloneDeep(parseRow(result.values[0]) as Defect)
      }
    }
    if (!shapeParams) return
    const originValue = cloneDeep(shapeParams[column as keyof Defect])
    await UpdateShapeOnly(shapeDatabaseId, column, value)

    undoRedo.execute(
      '결함 데이터 업데이트가',
      async () => {
        await UpdateShapeOnly(shapeDatabaseId, column, originValue)
      },
      async () => {
        await UpdateShapeOnly(shapeDatabaseId, column, value)
      },
    )
  }
  async function getQuickInputOtherItems(isRange: boolean): Promise<QuickInputOtherItem> {
    // SQLite에서 width/length만 조회 (shapes.value 전체 cloneDeep 제거)
    const db = await SQL.getDatabase()
    const result = await db.query(
      `SELECT width, length FROM ${storeKey} WHERE task_id = ? AND (deleted_at IS NULL OR deleted_at = '')`,
      [currentTaskDatabaseId.value],
    )
    const rows = (result.values ?? []) as Array<{ width: string; length: string }>

    const widthValues: string[] = []
    const lengthValues: string[] = []

    for (const item of rows) {
      if (item.width !== undefined && item.width !== '' && item.width !== '0') {
        if (!WIDTH_VALUES_RANGE.includes(item.width)) {
          const isNumeric = !isNaN(Number(item.width))
          if (isRange ? !isNumeric : isNumeric) {
            widthValues.push(String(item.width))
          }
        }
      }
      if (item.length !== undefined && item.length !== '' && item.length !== '0') {
        if (!LENGTH_VALUES.includes(item.length)) {
          lengthValues.push(String(item.length))
        }
      }
    }

    const getTop2 = (values: string[]): string[] => {
      const counts: Record<string, number> = {}
      values.forEach(value => {
        counts[value] = (counts[value] || 0) + 1
      })

      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
      return sorted.slice(0, 2).map(item => item[0])
    }

    const top2Widths = getTop2(widthValues)
    const top2Lengths = getTop2(lengthValues)

    return {
      width: top2Widths,
      length: top2Lengths,
    }
  }

  async function addTripodShapeToHistory(
    newId: string,
    tripodExportData: CanvasEngineExportEntityType,
    targetShapeIds: string[],
  ): Promise<Defect> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()

    // 기존 shapes 백업 (undo용)
    const originalShapes = targetShapeIds
      .map(id => shapes.value.find(shape => shape.localStringId.toString() === id))
      .filter((shape): shape is Defect => !!shape)

    // TRIPOD 생성
    await addTripodShape(newId, tripodExportData, targetShapeIds)

    // 새로 생성된 TRIPOD shape 찾기
    const newTripodShape = shapes.value.find(shape => shape.localStringId === newId)
    if (!newTripodShape) {
      throw new Error('TRIPOD 생성 후 shape를 찾을 수 없습니다.')
    }

    // undo/redo 등록
    undoRedo.execute(
      'TRIPOD 생성',
      // undo: TRIPOD 삭제 및 원본 shapes 복원
      async () => {
        await deleteShape(newTripodShape.id.toString())
        canvasStore.canvasDeleteShape(newTripodShape.id.toString())

        // 원본 shapes 복원
        for (const originalShape of originalShapes) {
          addShapeJobToQueue(originalShape)
          canvasStore.canvasAddShape(originalShape)
        }
      },
      // redo: TRIPOD 재생성
      async () => {
        // 원본 shapes 다시 삭제
        for (const originalShape of originalShapes) {
          await deleteShape(originalShape.id.toString())
        }

        // TRIPOD 재생성
        await addTripodShape(newId, tripodExportData, targetShapeIds)
        const redoTripodShape = shapes.value.find(shape => shape.localStringId === newId)
        if (redoTripodShape) {
          canvasStore.canvasAddShape(redoTripodShape)
        }
      },
    )

    return newTripodShape
  }

  /**
   * TRIPOD shape을 개별 POINT shape들로 분해합니다 (undo/redo 지원)
   * @param tripodShape - 분해할 TRIPOD shape
   * @param newPointEntities - 생성된 POINT entities
   */
  async function ungroupTripodShapeToHistory(
    tripodShape: Defect,
    newPointEntities: { id: string; shape_data: CanvasEngineExportEntityType }[],
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()

    // 원본 TRIPOD shape 백업
    const originalTripod = cloneDeep(tripodShape)

    try {
      // TRIPOD의 dots 데이터 추출
      const tripodShapeData = originalTripod.shape_data
      if (!tripodShapeData.tripodShapePoints || !tripodShapeData.tripodShapePoints.dots) {
        throw new Error('TRIPOD 데이터를 찾을 수 없습니다.')
      }

      const { dots } = tripodShapeData.tripodShapePoints
      if (dots.length === 0) {
        throw new Error('분해할 점이 없습니다.')
      }

      // 생성된 POINT shapes 저장
      const createdPointShapes: Defect[] = []

      // 각 POINT entity를 데이터베이스에 저장
      for (const newPointEntity of newPointEntities) {
        const newPointShape = {
          ...tripodShape,
          id: `${newPointEntity.id}_need_to_upload`,
          shape_data: newPointEntity.shape_data,
          selected_tags: [`${getUnixtime()}`],
          shape_type: SHAPES.POINT,
          localStringId: newPointEntity.id,
          task_id: Number(currentTaskDatabaseId.value),
          modifiedFromLocal: true,
          deleted_at: null,
        }
        const addedPointShape = await AddOnData(newPointShape)
        createdPointShapes.push(addedPointShape)
        canvasStore.canvasAddShape(addedPointShape)

        //사진 업로드
        const defectPhotoStore = useDefectPhotoStore()
        await defectPhotoStore.addDefectPhotoAlreadyUploaded(addedPointShape.localStringId)
      }

      // TRIPOD 삭제
      await deleteShape(tripodShape.localStringId)
      canvasStore.canvasDeleteShape(tripodShape.localStringId)

      // undo/redo 등록
      undoRedo.execute(
        '결합 해제가',
        // undo: 생성된 POINT들 삭제하고 TRIPOD 복원
        async () => {
          // 생성된 POINT들 삭제
          for (const pointShape of createdPointShapes) {
            await deleteShape(pointShape.localStringId)
            canvasStore.canvasDeleteShape(pointShape.localStringId)
          }

          // TRIPOD 복원
          await restoreShape(originalTripod.localStringId)
          const newShapeResult = shapes.value.find(
            item => item.localStringId == originalTripod.localStringId,
          )
          if (!newShapeResult) return
          canvasStore.canvasAddShape(newShapeResult)
          updateShapes()
        },
        // redo: TRIPOD 삭제하고 POINT들 다시 생성
        async () => {
          // TRIPOD 삭제
          await deleteShape(originalTripod.localStringId)
          canvasStore.canvasDeleteShape(originalTripod.localStringId)

          // POINT들 복원
          for (const pointShape of createdPointShapes) {
            await restoreShape(pointShape.localStringId)
            const newShapeResult = shapes.value.find(
              item => item.localStringId == pointShape.localStringId,
            )
            if (!newShapeResult) return
            canvasStore.canvasAddShape(newShapeResult)
            updateShapes()
          }
        },
      )
    } catch (error) {
      console.error('TRIPOD 분해 중 오류:', error)
      throw error
    }
  }

  // 여러 shape의 속성을 업데이트
  async function updateMultipleShapeToHistory(
    shapeIds: string[],
    updateFunc: (shape: Defect) => Partial<Defect>,
    historyMessage: string,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()

    // 대상 shape들 찾기
    const targetShapes = shapes.value.filter(
      shape => shapeIds.includes(shape.localStringId) && !shape.deleted_at,
    )
    if (targetShapes.length === 0) return

    // 이전 데이터 백업
    const originalShapes = cloneDeep(targetShapes)

    // 업데이트된 shape들 생성
    const updatedShapes = targetShapes.map(shape => {
      const updates = updateFunc(shape)

      return {
        ...shape,
        ...updates,
        shape_data: updates.shape_data
          ? { ...shape.shape_data, ...updates.shape_data }
          : shape.shape_data,
        modifiedFromLocal: true,
      }
    })

    // 메모리 업데이트
    shapes.value = shapes.value.map(shapeItem => {
      const updatedShape = updatedShapes.find(
        param => param.localStringId === shapeItem.localStringId,
      )
      return updatedShape ? updatedShape : shapeItem
    })

    // 배치 업데이트
    await requestUpdate(updatedShapes)

    // 캔버스 엔티티들에도 변경 사항 적용
    updatedShapes.forEach(shape => {
      const entity = canvasStore.getEntityById(shape.localStringId)
      if (entity && shape.shape_data) {
        // 벌룬 채우기 색상 업데이트
        if (shape.shape_data.balloonFillColor) {
          entity.setBalloonFillColor(shape.shape_data.balloonFillColor)
        }

        // 벌룬 폰트 색상 업데이트
        if (shape.shape_data.balloonFontColor) {
          entity.setBalloonFontColor(shape.shape_data.balloonFontColor)
        }

        // 도형 색상 업데이트
        if (shape.shape_data.color) {
          entity.changeColor(shape.shape_data.color)
        }

        // 렌더링 강제 업데이트
        entity.redraw()
      }
    })

    undoRedo.execute(
      historyMessage,
      // 복원 (undo)
      async () => {
        shapes.value = shapes.value.map(shapeItem => {
          const originalShape = originalShapes.find(
            param => param.localStringId === shapeItem.localStringId,
          )
          return originalShape ? { ...originalShape } : shapeItem
        })

        await requestUpdate(originalShapes)
        originalShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
      },
      // 재생성 (redo)
      async () => {
        shapes.value = shapes.value.map(shapeItem => {
          const updatedShape = updatedShapes.find(
            param => param.localStringId === shapeItem.localStringId,
          )
          return updatedShape ? { ...updatedShape } : shapeItem
        })

        await requestUpdate(updatedShapes)
        updatedShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
      },
    )
  }

  /////////////////////코드정리///////////////////////////
  async function addShapeToHistory(
    _shape: CanvasEngineExportEntityType,
    floorplanId: string,
    parentDefectId?: string | number,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const defectPhotoStore = useDefectPhotoStore()
    const isBrush = _shape.type === 'BRUSH'
    // 빈 색상 방어: 서버가 color 빈 값을 400으로 거절해 생성 job이 무한 재시도에 갇힌다(2026-10-21 DIRECTION 사례).
    // 폴백 순서는 도면 없이 결함 추가(addShapelessDefect)와 같다. 색상 규칙(defect.ts)은 건드리지 않는다.
    const color =
      _shape.color ||
      canvasStore.getCanvasEngine()?.forCreateBalloonBorderColor ||
      getCurrentColor('red') ||
      '#FF0000'
    if (color !== _shape.color) {
      console.warn('[ShapeStore] 생성 색상이 비어 기본색으로 대체:', _shape.type, _shape.shapeId)
      canvasStore.getEntityById(_shape.shapeId)?.changeColor(color)
    }
    // 먼저 shape 생성
    //1. 추가에 필요한 데이터
    const newShape = {
      shape_type: _shape.type,
      floorplan_id: Number(floorplanId),
      template: isBrush ? 'canvas-brush' : ('custom-RC' as ShapeTemplateType),
      color,
      task_id: Number(currentTaskDatabaseId.value),
      memo: '',
      progressing: 0,
      width: '',
      length: '',
      area: '',
      count: '',
      is_structural: 0,
      repair_plan: '',
      cause_estimate: '',
      structural_component: '',
      classification: '',
      sort_key: getLastSortKey(
        shapes.value.filter(
          defect =>
            `${defect.floorplan_id}` == `${floorplanId}` &&
            !defect.deleted_at &&
            defect.shape_type !== SHAPES.BRUSH &&
            (!isPreviousShape(defect.previous_defect_id?.toString() ?? '') ||
              (isPreviousShape(defect.previous_defect_id?.toString() ?? '') &&
                defectPhotoStore.getIsPreviousHasDefectPhoto(defect.localStringId.toString()))),
        ),
      ),
      shape_data: {
        shapeId: _shape.shapeId,
        areaBorderThicknessScale: _shape.areaBorderThicknessScale,
        balloonFillColor: _shape.balloonFillColor,
        balloonFontColor: _shape.balloonFontColor,
        balloonFontScale: _shape.balloonFontScale,
        balloonPoint: _shape.balloonPoint,
        balloonScale: _shape.balloonScale,
        color,
        connectedLineThicknessScale: _shape.connectedLineThicknessScale,
        height: _shape.height,
        point: _shape.point,
        pointShapeScale: _shape.pointShapeScale,
        points: _shape.points,
        rotate: _shape.rotate,
        type: _shape.type,
        width: _shape.width,
        brushLines: _shape.brushLines || [],
        dotType: _shape.dotType,
      },
      ...(parentDefectId ? { parent_defect_id: parentDefectId } : {}),
    }
    await addShape(newShape)
    const currentShapeId = _shape.shapeId
    // 결함 생성 + 자동 입력을 하나의 undo 엔트리로 병합하기 위해 batch 시작
    // brush는 DefectPanel 자동 입력이 없으므로 batch 불필요
    if (!isBrush) undoRedo.startBatch()
    // undo/redo 등록
    undoRedo.execute(
      '결함 생성이',
      // 삭제 (undo)
      async () => {
        await deleteShape(_shape.shapeId)
        canvasStore.canvasDeleteShape(_shape.shapeId)
      },
      // 재생성 (redo)
      async () => {
        await restoreShape(currentShapeId)
        const newShapeResult = shapes.value.find(item => item.localStringId == currentShapeId)
        if (!newShapeResult) return
        canvasStore.canvasAddShape(newShapeResult)
        updateShapes()
      },
    )
    // batch는 DefectPanel.initData에서 endBatch('결함 생성이')로 종료됨
  }

  // 도면 없이 결함 추가
  async function addShapelessDefect(floorplanId: string): Promise<Defect | undefined> {
    const defectPhotoStore = useDefectPhotoStore()
    const currentShapes = shapes.value.filter(
      defect =>
        `${defect.floorplan_id}` == `${floorplanId}` &&
        !defect.deleted_at &&
        defect.shape_type !== SHAPES.BRUSH &&
        (!isPreviousShape(defect.previous_defect_id?.toString() ?? '') ||
          (isPreviousShape(defect.previous_defect_id?.toString() ?? '') &&
            defectPhotoStore.getIsPreviousHasDefectPhoto(defect.localStringId.toString()))),
    )
    const canvasStore = useCanvasStore()
    const canvasColor = canvasStore.getCanvasEngine()?.forCreateBalloonBorderColor
    const defectColor = canvasColor || getCurrentColor('red') || '#FF0000'
    const newShape: Partial<Defect> = {
      shape_type: 'NO_SHAPE',
      shape_data: null,
      floorplan_id: Number(floorplanId),
      template: 'custom-RC' as ShapeTemplateType,
      color: defectColor,
      task_id: Number(currentTaskDatabaseId.value),
      memo: '',
      progressing: 0,
      width: '',
      length: '',
      area: '',
      count: '',
      is_structural: 0,
      repair_plan: '',
      cause_estimate: '',
      structural_component: '',
      classification: '',
      sort_key: getLastSortKey(currentShapes),
    }
    await addShape(newShape)
    return shapes.value[shapes.value.length - 1]
  }

  async function addShape(newShape: Partial<Defect>): Promise<void> {
    const cloneShape: Partial<Defect> = cloneDeep(newShape)
    if (cloneShape.shape_data) {
      cloneShape.id = `${cloneShape.shape_data.shapeId}_need_to_upload`
      cloneShape.localStringId = cloneShape.shape_data.shapeId
    } else {
      const syntheticId = `no-shape-${crypto.randomUUID()}`
      cloneShape.id = `${syntheticId}_need_to_upload`
      cloneShape.localStringId = syntheticId
    }
    cloneShape.created_at = Merge.getCurrentTime()
    cloneShape.defect_photos = [] as DefectPhoto[]
    cloneShape.modifiedFromLocal = true

    const allTaskStore = useAllTaskStore()

    // floorplan_id로부터 facility_id를 역추적하여 설정
    if (cloneShape.floorplan_id && !cloneShape.facility_id) {
      const facility = allTaskStore.currentTaskData?.facilities.find(f =>
        f.floorplans.some(fp => fp.id == cloneShape.floorplan_id),
      )
      if (facility) {
        cloneShape.facility_id = facility.id
      }
    }

    const taskType = allTaskStore.taskType
    if (taskType == 'detailedSafetyInspection') {
      cloneShape.inspection_type = 'TEST_MEASURE'
    } else {
      cloneShape.inspection_type = 'STATUS_OUTSIDE'
    }

    shapes.value.push(cloneShape as Defect)
    const changes: ChangedItem[] = findChangedItems([], [cloneShape as Defect])
    logChanges('SHAPES', changes)
    // 저장은 백그라운드로 수행 — 생성 직후 패널 오픈이 SQLite INSERT를 기다리지 않는다.
    // 메모리 반영은 위 push로 완료됐고, 후속 수정/업로드 큐잉은 같은 저장 경로의
    // 순서를 따르므로 INSERT 이후에 적용된다.
    void requestUpdate([cloneShape as Defect]).catch(error => {
      console.error(
        `[PhotoGuard] shape 생성 저장 실패 (localStringId=${cloneShape.localStringId}):`,
        error,
      )
      App.toastInstance.add('error', '결함 저장에 실패했습니다. 저장공간을 확인해주세요.', 6000)
    })
  }

  //삭제
  /**
   * shape를 삭제합니다 (소프트 삭제)
   * @param shapeId - 삭제할 shape의 ID
   * @returns Promise<void>
   */
  async function deleteShape(shapeId: string | number): Promise<void> {
    const findShape = shapes.value.find(item => item.id == shapeId || item.localStringId == shapeId)
    if (!findShape) return
    const changedShape = cloneDeep(findShape)
    changedShape.deleted_at = Merge.getCurrentTime()

    // 변경된 필드 추적 - deleted_at 필드만 업데이트
    const existingFieldsUpdatedAt = findShape.fields_updated_at || {}
    const fieldsUpdatedAt = Merge.setFieldUpdate(existingFieldsUpdatedAt, 'deleted_at')
    fieldsUpdatedAt.need_restore = false
    changedShape.fields_updated_at = fieldsUpdatedAt
    changedShape.modifiedFromLocal = true

    if (
      (isNaN(Number(findShape.id)) || findShape.id.toString().includes('_need_to_upload')) &&
      changedShape.deleted_at
    ) {
      await deleteNotUploadShape(shapeId.toString(), changedShape, findShape)
      return
    }

    const changes: ChangedItem[] = findChangedItems([findShape], [changedShape])
    logChanges('SHAPES', changes)
    await requestUpdate([changedShape])
  }

  async function deleteMultipleShapes(shapeIds: string[]): Promise<void> {
    try {
      const deletedTime = Merge.getCurrentTime()
      const changedShapes: Defect[] = []
      const currentChangedItems: ChangedItem[] = []

      // 1. 원본 배열을 한 번의 map으로 순회하며 업데이트 및 변경 로그 생성
      const shapesToProcess = shapes.value.filter(shape =>
        shapeIds.includes(shape.localStringId.toString()),
      )
      shapesToProcess.forEach(shape => {
        const fieldsUpdatedAt = Merge.setFieldUpdate(shape.fields_updated_at || {}, 'deleted_at')
        fieldsUpdatedAt.need_restore = false
        const updatedShape = {
          ...shape,
          deleted_at: deletedTime,
          fields_updated_at: fieldsUpdatedAt,
          modifiedFromLocal: true,
        } as Defect
        changedShapes.push(updatedShape)
        const changes = findChangedItems([shape], [updatedShape])
        currentChangedItems.push(...changes)
      })

      // 2. 삭제할 대상이 없는 경우 조기 종료
      if (changedShapes.length === 0) return

      // 3. 로그 기록 및 서버 업데이트 요청
      logChanges('SHAPES', currentChangedItems)
      await requestUpdate(changedShapes)
    } catch (error) {
      console.error('Defect 삭제 오류:', error)
    }
  }

  async function restoreShape(shapeId: string): Promise<void> {
    const findShape = shapes.value.find(shape => `${shape.localStringId}` == `${shapeId}`)
    if (!findShape) return
    const previousShape = cloneDeep(findShape)

    findShape.deleted_at = null // undo할 항목임 (음수 타임스탬프)

    // 변경된 필드 추적 - deleted_at 필드만 업데이트
    const existingFieldsUpdatedAt = findShape.fields_updated_at || {}
    const fieldsUpdatedAt = Merge.setFieldUpdate(existingFieldsUpdatedAt, 'deleted_at')
    fieldsUpdatedAt.need_restore = true
    findShape.fields_updated_at = fieldsUpdatedAt
    findShape.modifiedFromLocal = true

    if (isNaN(Number(findShape.id)) || findShape.id.toString().includes('_need_to_upload')) {
      await restoreNotUploadShape(shapeId.toString())
    }

    const changes: ChangedItem[] = findChangedItems([previousShape], [findShape])
    logChanges('SHAPES', changes)
    await requestUpdate([findShape])
  }

  async function restoreShapes(shapeData: Defect[]): Promise<void> {
    const shapeIds = shapeData.map(shape => shape.localStringId)
    const findShapes = shapes.value.filter(shape => shapeIds.includes(shape.localStringId))
    if (!findShapes) return

    try {
      const changedShapes: Defect[] = []
      const currentChangedItems: ChangedItem[] = []

      const shapesToProcess = shapes.value.filter(shape =>
        shapeIds.includes(shape.localStringId.toString()),
      )
      shapesToProcess.forEach(shape => {
        const fieldsUpdatedAt = Merge.setFieldUpdate(shape.fields_updated_at || {}, 'deleted_at')
        fieldsUpdatedAt.need_restore = true
        const updatedShape = {
          ...shape,
          deleted_at: null,
          fields_updated_at: fieldsUpdatedAt,
          modifiedFromLocal: true,
        } as Defect
        changedShapes.push(updatedShape)
        const changes = findChangedItems([shape], [updatedShape])
        currentChangedItems.push(...changes)
      })

      // 2. 삭제할 대상이 없는 경우 조기 종료
      if (changedShapes.length === 0) return

      // 3. 로그 기록 및 서버 업데이트 요청
      logChanges('SHAPES', currentChangedItems)
      await requestUpdate(changedShapes)
    } catch (error) {
      console.error('Defect 삭제 오류:', error)
    }
  }
  return {
    UpdateValue,
    shapes: shapesComputed,
    originShapes: shapes,
    notUploadedShapes,
    UpdateShape,
    recommandedShapesComputed,
    GetItem,
    GetItemRaw,
    shapeServerIdMap,
    currentFloorBranchShapes,
    initData,
    requestUpdate,
    uploadNotUploaded,
    getSortedShapes,
    getFloorShapesUnsorted,
    getFloorShapesFromDB,
    getAllShapeRefsFromDB,
    getUnplacedPrevCandidatesFromDB,
    loadFloorplanShapes,
    getShapesForReadOnly,
    getFacilityShapes,
    getFacilityNumberingShapes,
    getNumberingCountShapes,
    isCheckPreviousShape,
    setIsBecomeCurrent,
    UpdateShapeOnly,
    copyShape,
    allClear,
    getAllModifiedData,
    addApiQueue,
    addShapeToHistory,
    addShapelessDefect,
    copyShapeToHistory,
    updateShapeToHistory,
    updateColorToHistory,
    updateShapeOnlyToHistory,
    deleteShapeToHistory,
    deleteMultipleShapesToHistory,
    getQuickInputOtherItems,
    getShapes,
    getCurrentRelatedShapes,
    updateFloorOrFacilityShapes,
    getUpdateTargetShapes,
    addTripodShape,
    addTripodShapeToHistory,
    ungroupTripodShapeToHistory,
    updateMultipleShapeToHistory,
    getCurrentShape,
    updateShapes,
    getCurrentDefect,
    updateSortKey,
    updateSortKeyToHistory,
    deleteNotUploadShape,
    restoreNotUploadShape,
    shapeCount: computed(() => shapes.value.length),
    getTotalShapeCount,
  }
})
